// Joana chat shell — the orchestrator. Wires the store, the REST client, the
// realtime socket, and the surface together, and implements every shell action:
// login (+ 2FA), loading chats, opening a conversation (history + live channel),
// composing + sending (optimistic echo → server ack), typing presence, and
// logout. This is the first-party WaveTalk chat surface running entirely on Joana
// (ADR-0011) — no isolate, talking to Phoenix REST :7419 + Channels :7421.

import { ApiClient, ApiError, isZoneUpdateRequired } from "./api-client.ts";
import type { Channel, Scheduler, Socket } from "./phoenix-channel.ts";
import { Surface, type RendererFactory } from "./surface.ts";
import { ShellStore, chatById, type ShellState } from "./store.ts";
import { fromRoster, namedByRollup, toAsk } from "./author-names.ts";
import { installAvatarSource, installStatusSource } from "./views.ts";
import { buildTree } from "./views.ts";
import {
  isTwoFactor,
  type Chat,
  type LoginResponse,
  type LoginResult,
  type Message,
  type MiniAppListItem,
  type SignalLinkCredential,
  type User,
} from "./types.ts";
import { LANGUAGE_KEY, browserTags, decodeLanguage, resolveLanguage, translator } from "../i18n/index.ts";
import { THEME_KEY, decodeTheme, type ThemeChoice } from "./appearance.ts";
import {
  WALLPAPER_KEY,
  decodeWallpaper,
  wallpaperById,
  wallpaperCss,
  type ChatWallpaper,
} from "./wallpaper.ts";
// TYPE-ONLY, and it stays that way: the shell names what a decrypted message
// looks like and how the inbox is reached, and can construct neither. Both are
// erased at build time, so this import adds nothing to the shell bundle and no
// path from here into the crypto.
import type { InboxTransport } from "../signal/encrypted-inbox.ts";
import type { CachedMessage as DecryptedMessage } from "../signal/message-cache.ts";

/** The first-party sample, always offered in the picker (resolved locally, so it
 *  works even with an empty/unreachable registry). app_id "sample" is the token
 *  the browser glue maps to the built-in package. */
/** One page of the channel directory (ADR-0024 A16). */
const EXPLORE_PAGE = 20;
/** `@default_page` in `Sema.Api.Channels`: a shorter page means the end. */
const CHANNEL_PAGE = 20;

/**
 * How long a history bundle (ADR-0027 D4.7) may go quiet before the browser
 * stops claiming to be syncing.
 *
 * Generous, because the gaps between batches are a phone's gaps: a screen that
 * locked, a walk through a dead spot, a server that answered "backlog full"
 * and asked for thirty seconds. Short enough that a phone which has genuinely
 * stopped does not leave a sentence standing for the rest of the session.
 */
const HISTORY_SYNC_STALL_MS = 90_000;

const BUILTIN_SAMPLE: MiniAppListItem = {
  id: "sample",
  app_id: "tz.wavetalk.sample.playground",
  name: "Joana Playground",
  description: "A built-in sample mini-app",
  builtin: true,
};

/**
 * The seam between the shell and the signal worker (ADR-0027 D4.4, D4.6).
 *
 * Deliberately a handful of named verbs and not the worker client itself: the
 * shell has no business reaching the crypto directly, and an interface this
 * small is one a reviewer can check against D4.2's "no capability reaches the
 * signal module".
 *
 * It grew from two methods to five for D4.8 step 5, and the shape of the growth
 * is the point: the shell still cannot encrypt, decrypt, export a key or name a
 * peer. What it gained is "read whatever is addressed to me" and "show me what
 * I already read" — both of which hand back PLAINTEXT MESSAGES, which is the
 * one thing that was always going to have to cross this line.
 */
export interface SignalDeviceLink {
  /** Open (or create) this browser's device and return its PUBLIC identity —
   *  the 33 bytes the QR carries, base64 — plus its registration id. */
  identity(): Promise<{ identityKey: string; registrationId: number }>;
  /** Record the device number a primary assigned, then publish this browser to
   *  the key directory. Returns nothing: the caller has no use for the row and
   *  should not learn to depend on one.
   *
   *  `accountId` is PASSED, never read from the shell's state. The publish is
   *  deliberately run before the session is adopted (see `linkSignalDevice`),
   *  so at that instant the store does not yet know who signed in — an
   *  implementation that looked the account up for itself would find nothing
   *  and refuse, on every real sign-in, silently. It did: s157 linked a browser
   *  against the live stack and the device row was never written. */
  adoptAndPublish(accountId: string, deviceNumber: number, linkSignature: string): Promise<void>;
  /** Open this browser's device for the account now signed in, and say whether
   *  there IS one. Asked of the vault rather than of a flag: being a device
   *  outlives the page that linked it. */
  attach(): Promise<boolean>;
  /** Drain the ciphertext addressed to this device over `transport`, calling
   *  `onMessage` for each message that becomes readable — after it is durable.
   *  Resolves with how many that was. */
  drain(
    transport: InboxTransport,
    onMessage: (m: DecryptedMessage) => void,
    /** ADR-0027 D4.7 — how far through a history bundle we are. Reported from
     *  the frame's own numbering, so a redelivery cannot move it backwards. */
    onSyncProgress?: (seq: number, total: number) => void,
  ): Promise<number>;
  /** What this browser has already decrypted for one chat. The server cannot
   *  answer this: it purged the ciphertext when this device acked it. */
  readable(chatId: string): Promise<DecryptedMessage[]>;
  /**
   * ADR-0007 — the 60-digit number to compare out-of-band with `peerUserId`.
   *
   * Public material, which is why it may cross this line at all: a
   * fingerprint is a hash of two identity PUBLIC keys and reveals no secret.
   * The shell still cannot encrypt, decrypt, export or name a peer.
   *
   * `null` covers three states the caller must not tell apart on screen,
   * because none of them is "you are being attacked": this browser is not a
   * device, the peer has published none, or there is no session with any of
   * their devices yet — the last being ordinary, and the reason the phones say
   * "it appears once you've exchanged an encrypted message".
   *
   * It answers with the device NUMBER it fingerprinted alongside the digits.
   * A safety number is computed between two DEVICE identity keys, so naming
   * only the person would let a screen imply this number is the account's.
   */
  safetyNumber(peerUserId: string): Promise<{ digits: string; peerDevice: number } | null>;
}

export interface ShellOptions {
  store: ShellStore;
  api: ApiClient;
  rendererFactory: RendererFactory;
  /** Build a realtime socket. `connectParams` is the async auth-param provider the
   *  socket calls for EVERY (re)connect — here it mints a fresh single-use WS
   *  ticket over REST, so the access token never rides the socket URL. Injected so
   *  tests supply a fake-transport socket. */
  createSocket: (connectParams: () => Promise<Record<string, string>>) => Socket;
  /** ISO timestamp source (injected for deterministic tests). */
  now?: () => string;
  /** client_msg_id generator (injected for deterministic tests). */
  genId?: () => string;
  /** Persist the rotating refresh token (e.g. to storage). `null` means FORGET
   *  it — a sign-out, or a session the server has refused. */
  onRefreshToken?: (refreshToken: string | null) => void;

  /** Read back the refresh token this browser stored on a previous visit, if
   *  any. Supplying it is what makes a reload keep the session instead of
   *  landing on the login screen. */
  restoreRefreshToken?: () => string | null;
  /** Put the chosen appearance on the document (`<html data-appearance>`).
   *  The host's job: the shell has no document, and this is the one setting
   *  that is CSS rather than tree. */
  onAppearance?: (choice: ThemeChoice) => void;
  /** G16 — publish the chosen wallpaper's CSS (a `--wt-wallpaper-canvas`
   *  custom property for both appearances). The host's job for the same reason
   *  `onAppearance` is: the shell has no document, and a canvas that lives in
   *  the tree cannot follow a `prefers-color-scheme` change, which runs no
   *  render pass. */
  onWallpaper?: (css: string) => void;
  /** ADR-0033 D9 — this bundle's identity, echoed onto the socket so the
   *  realtime service judges it by the same floor the API does. */
  clientBuild?: { platform: string; build: number };
  /** ADR-0033 D9 — reload the page. The web's forced update is not an install,
   *  it is a fresh bundle, and the shell has no `location` to reach for. */
  onReload?: () => void;
  /** Open an embedded mini-app (the browser glue launches it in the sandboxed
   *  overlay via MiniAppHost). The shell only routes the intent + tracks UI
   *  state; it never touches the sandbox itself. */
  onOpenMiniApp?: (appId: string, title: string) => void;
  /** Close the embedded mini-app. */
  onCloseMiniApp?: () => void;
  /** Timer source for the picker search debounce (injected for tests). */
  scheduler?: Scheduler;
  /** Debounce window (ms) before a search keystroke hits the registry. */
  searchDebounceMs?: number;
  /** Interval (ms) between QR login status polls. */
  qrPollIntervalMs?: number;
  /**
   * This browser's Signal device (ADR-0027 D4). Absent in a build without the
   * engine and in every test that is not about linking — which is why the whole
   * QR flow below treats it as optional rather than assuming it: a browser
   * without one signs in exactly as it always has, and does not become a
   * device.
   */
  signal?: SignalDeviceLink;

  // --- Avatars (ADR-0022). Host-owned pieces, injected like the mini-app
  // overlay: the loader turns (owner, version) into blob: URLs with the
  // Bearer an <img> cannot carry; the sheets are trusted-shell DOM.
  avatars?: {
    peek(ref: { kind: "user" | "chat"; ownerId: string; version: string; rendition: "thumb" | "full" }): string | null;
    forgetMisses(): void;
    clear(): void;
  };
  /** D8 — status media, the same arrangement as `avatars` above: the loader
   *  turns (status id, rendition) into blob: URLs with the Bearer an `<img>`
   *  cannot carry, and `sweepExpired` drops what the tray no longer holds,
   *  because a cache that outlives a 24-hour promise breaks it. */
  statusMedia?: {
    peek(ref: { statusId: string; rendition: "full" | "thumb" | "poster" | "video" | "voice" }): string | null;
    sweepExpired(liveIds: Set<string>): void;
    forgetMisses(): void;
    clear(): void;
  };
  /** Pick + crop a photo, answering both renditions and the chosen policy. */
  pickAvatarPhoto?: (visibility: { policy: string } | null) => Promise<{ result: { full: Blob; thumb: Blob }; policy: string } | null>;
  /** Grid pickers + a text ask (display name). */
  pickFromGrid?: (title: string, choices: { key: string; label?: string; imageUrl?: string; emoji?: string }[]) => Promise<string | null>;
  askText?: (title: string, initial: string) => Promise<string | null>;
}

export class Shell {
  private readonly store: ShellStore;
  private readonly api: ApiClient;
  private readonly surface: Surface;
  private readonly now: () => string;
  private readonly genId: () => string;

  /**
   * The words, in the language this account reads.
   *
   * A METHOD and not a cached field: ADR-0028 lets the choice change inside a
   * session, and a translator captured at construction would keep drawing the
   * language the account had when the page loaded. `views.ts` solves the same
   * problem by re-deriving `t` at its single entry point; the shell has many
   * entry points, so it re-derives per call. The lookup is a property read on
   * a small object.
   */
  private t(key: string, ...args: (string | number)[]): string {
    return translator(resolveLanguage(this.store.state.uiLanguage, browserTags()))(key, ...args);
  }

  private socket: Socket | null = null;
  /** The debounced Explore search, so a fast typist does not race themselves. */
  private exploreTimer: number | null = null;
  private activeChannel: Channel | null = null;
  private refreshToken: string | null = null;
  private typingSent = false;
  /** QR login session (sid + the tab-held client secret) + its poll loop.
   *  `qrSeq` invalidates stale create/poll responses after a refresh/switch. */
  private qrSeq = 0;
  private qrSession: { sid: string; secret: string } | null = null;
  private qrPollHandle: unknown = null;
  /** Monotonic token so out-of-order app-list/search responses can't clobber a
   *  newer one (the user switched tab or kept typing). */
  private appsSeq = 0;
  private readonly scheduler: Scheduler;
  private readonly searchDebounceMs: number;
  private readonly qrPollIntervalMs: number;
  /** ADR-0027 D4 — this browser's Signal device, or null in a build without
   *  the engine. Null is a supported state, not a missing dependency. */
  private readonly signal: SignalDeviceLink | null;
  private searchDebounceHandle: unknown = null;

  private readonly opts: ShellOptions;

  constructor(opts: ShellOptions) {
    this.opts = opts;
    this.store = opts.store;
    this.api = opts.api;
    // ADR-0033 D9 — the server refuses this BUNDLE. Raise the wall and leave
    // the session where it is: signing out would send the person to a login
    // screen that would refuse them for the same reason, one screen further
    // from the answer.
    this.api.onUpdateRequired = () => this.store.setUpdateRequired();
    // A rotation happened (on load, or behind a 401). Both tokens are new —
    // persist the refresh one or the NEXT reload is a sign-in screen again.
    this.api.onTokensRefreshed = (_access, refresh) => {
      this.refreshToken = refresh;
      this.opts.onRefreshToken?.(refresh);
    };
    // The server refused the refresh token itself: this session really is over.
    // Fired only for that, never for a failure we could not interpret.
    this.api.onSessionEnded = () => this.forgetSession();
    if (opts.avatars) {
      const avatars = opts.avatars;
      installAvatarSource((kind, ownerId, version, rendition) =>
        avatars.peek({ kind, ownerId, version, rendition }),
      );
    }
    if (opts.statusMedia) {
      const media = opts.statusMedia;
      installStatusSource((statusId, rendition) => media.peek({ statusId, rendition }));
    }
    this.now = opts.now ?? (() => new Date().toISOString());
    this.genId = opts.genId ?? (() => cryptoRandomId());
    this.scheduler = opts.scheduler ?? defaultScheduler();
    this.searchDebounceMs = opts.searchDebounceMs ?? 250;
    this.qrPollIntervalMs = opts.qrPollIntervalMs ?? 1500;
    this.signal = opts.signal ?? null;
    this.surface = new Surface(opts.rendererFactory, buildTree, (cmd, payload) => {
      void this.dispatch(cmd, payload);
    });
    // The store holds ONE subscriber, so everything that follows state rides here.
    this.store.subscribe((s) => {
      this.surface.render(s);
      this.nameUnnamedAuthors(s);
    });
  }

  /** s175 — which senders of the OPEN chat have been asked about (author-names.ts). */
  private askedAbout: { chatId: string | null; ids: Set<string> } = { chatId: null, ids: new Set() };
  /** What the last ask looked at. The store has ONE subscriber, so this runs on
   *  every keystroke and typing event; like the phones, it only looks again when
   *  the thread or the chat row changed — otherwise a failing roster call would
   *  be retried once per keystroke. */
  private lastLooked: { messages: unknown; members: unknown } = { messages: null, members: null };

  /**
   * A group's author past the rollup's bound of 8 read "Unknown". When the open
   * thread holds a message from someone nobody has named, ask the roster —
   * once per sender, and again only if the asking itself failed.
   */
  private nameUnnamedAuthors(s: ShellState): void {
    const chatId = s.activeChatId;
    if (chatId !== this.askedAbout.chatId) this.askedAbout = { chatId, ids: new Set() };
    if (!chatId) return;
    // No asking before the row is known: without the rollup every sender looks unnamed.
    // `chatById`, not `chats`: a zone is never in Chats, and a zone of more
    // than eight voices is exactly the chat whose ninth needs the roster.
    const chat = chatById(s, chatId);
    if (!chat) return;
    if (this.lastLooked.messages === s.messages && this.lastLooked.members === chat.members) return;
    this.lastLooked = { messages: s.messages, members: chat.members };
    const named = namedByRollup(chat.members ?? []);
    for (const id of Object.keys(s.rosterNames)) named.add(id);
    const asked = this.askedAbout;
    const ask = toAsk(s.messages, named, asked.ids, s.me?.id ?? null);
    if (ask.size === 0) return;
    for (const id of ask) asked.ids.add(id);
    void this.api.chatRoster(chatId).then(
      ({ members }) => this.store.addRosterNames(chatId, fromRoster(members ?? [])),
      // Could not ask is not "nobody": the next change to the thread asks again.
      () => { for (const id of ask) asked.ids.delete(id); },
    );
  }

  /** Paint the initial state. Landing on the login screen in QR mode opens
   *  the scan session immediately — the WeChat model: the code IS the page. */
  start(): void {
    // ADR-0028 — the language this browser last chose, read before the first
    // paint so the page opens in it rather than in English until a profile
    // request returns. The ACCOUNT is the owner; `reconcile` below adopts its
    // value once we know who we are.
    try {
      this.store.setUiLanguage(decodeLanguage(localStorage.getItem(LANGUAGE_KEY)));
    } catch {
      // Storage blocked. The browser's own preference still applies.
    }
    // The appearance this browser last chose, applied BEFORE the first paint —
    // a page that opens light and turns dark a frame later is a flash, and the
    // flash is worse on the appearance somebody chose to avoid brightness.
    try {
      this.applyAppearance(decodeTheme(localStorage.getItem(THEME_KEY)));
    } catch {
      // Storage blocked. The machine's own preference still applies.
    }
    // G16 — and the canvas, for the same reason and before the same paint.
    try {
      this.applyWallpaper(decodeWallpaper(localStorage.getItem(WALLPAPER_KEY)));
    } catch {
      // Storage blocked. The default canvas is what everybody starts on.
    }
    this.surface.render(this.store.state);
    // A session this browser already has. Tried BEFORE the QR code goes up, so
    // a reload returns to the app rather than to a sign-in screen — which is
    // what every reload did until now, because the refresh token was written to
    // storage and never read back by anything.
    if (this.restoreSession()) return;
    const s = this.store.state;
    if (s.screen === "login" && s.loginMode === "qr") void this.startQrLogin();
  }

  /**
   * Exchange a stored refresh token for a live session.
   *
   * Returns whether an attempt is under way — the caller skips putting a QR
   * code up if so, since landing on a scan screen for half a second and then
   * jumping into the app is worse than waiting for the answer.
   *
   * Rotating here also winds the session's clock forward on every visit, so the
   * refresh window is measured from the last time somebody OPENED WaveTalk
   * rather than from the last time a request happened to be refused.
   */
  private restoreSession(): boolean {
    const stored = this.opts.restoreRefreshToken?.() ?? null;
    if (!stored) return false;
    this.refreshToken = stored;
    this.api.setRefreshToken(stored);
    this.store.setRestoringSession(true);
    void (async () => {
      try {
        const res = await this.api.refresh(stored);
        if (isTwoFactor(res)) {
          // `/auth/refresh` cannot answer this. Treat it as no session rather
          // than as a challenge nobody asked for.
          this.forgetSession();
          return;
        }
        await this.afterLoginAttempt(res);
      } catch (e) {
        // 401 — the stored token is dead (revoked, or older than the refresh
        // window). Forget it and ask for a sign-in, which is the honest answer.
        if (e instanceof ApiError && e.status === 401) {
          this.forgetSession();
          return;
        }
        // Anything else — offline, a 502, a 503 during a deploy — says nothing
        // about the session. KEEP the stored token: the next reload, once the
        // network or the server is back, restores normally. Throwing it away
        // here would sign somebody out over a bad minute, which is the whole
        // bug this path exists to fix.
        this.store.setSessionRestoreFailed(
          this.t("Couldn't reach WaveTalk. Check your connection and reload, or sign in below."),
        );
      } finally {
        this.store.setRestoringSession(false);
      }
    })();
    return true;
  }

  /** Drop the session locally — the credential is gone or refused. Separate
   *  from `doLogout`, which additionally tells the server. */
  private forgetSession(): void {
    this.api.setToken(null);
    this.api.setRefreshToken(null);
    this.refreshToken = null;
    this.opts.onRefreshToken?.(null);
    this.store.loginReset();
    void this.startQrLogin();
  }

  // --- action router -------------------------------------------------------

  private async dispatch(cmd: string, payload: Record<string, unknown>): Promise<void> {
    switch (cmd) {
      case "set_field":
        return this.store.setLoginField(
          payload.field as "loginUsername" | "loginPassword" | "twoFactorCode",
          str(payload.value),
        );
      case "login":
        return this.doLogin();
      case "verify_2fa":
        return this.doVerifyTwoFactor();
      case "login_mode":
        return this.switchLoginMode(payload.mode === "password" ? "password" : "qr");
      case "qr_refresh":
        return this.startQrLogin();
      case "open_chat":
        return this.openChat(str(payload.chat_id));
      // B9 — a tapped contact card. Opens the CARD, never a chat: the person
      // receiving a card did not choose to be handed it.
      case "open_contact":
        this.openContactCard(str(payload.user_id));
        return;
      case "contact_message":
        return void this.messageFromContactCard(str(payload.user_id));
      case "contact_close":
        this.store.setContactCard(null);
        return;
      // ADR-0007 — the safety-number screen. Reached only from an encrypted
      // conversation on a browser that IS a device; see `encryptionBar`.
      case "verify_open":
        this.openSafetyNumber();
        return;
      case "verify_close":
        this.store.setSafetyNumber(null);
        return;
      // ADR-0033 D9 — the web's forced update is not an install, it is a
      // fresh bundle. The host owns `location`; the shell only asks.
      case "reload":
        this.opts.onReload?.();
        return;
      case "back":
        // An open assistant/key editor is a step of its own: Back closes it
        // (dropping an issued key) rather than leaving the organisation.
        if (this.store.state.screen === "office_org" && this.store.state.orgEditor) {
          // Except the key shown ONCE: Back must not throw it away before it
          // is copied — Android blocks back there and iOS blocks the swipe.
          // Done is the way out (s199 review).
          if (this.store.state.orgEditor.kind !== "key_issued") this.store.setOrgEditor(null);
          return;
        }
        // Explore is reached FROM Updates, so back returns there. Falling
        // through to `backToChats` would drop somebody two screens for one
        // tap, which reads as the app losing its place.
        if (this.store.state.screen === "explore") {
          this.store.closeExplore();
          return;
        }
        // Office is three screens deep and each step back is one step, not a
        // jump to the chat list: dropping somebody two screens for one tap
        // reads as the app losing its place.
        if (this.store.state.screen === "office_publisher") {
          this.store.openOffice();
          return;
        }
        if (this.store.state.screen === "office_new") {
          this.store.openOffice();
          return;
        }
        if (this.store.state.screen === "office") {
          // Back through the door it was opened by — Profile or the World
          // segment. See `Store.openOffice`.
          this.store.closeOffice();
          return;
        }
        {
          // ADR-0018 — a zone is never in Chats; back returns to its
          // organisation, the place it was opened from.
          const open = chatById(this.store.state, this.store.state.activeChatId);
          if (this.store.state.screen === "conversation" && open?.chat_type === "zone" && open.org_id) {
            this.leaveActive();
            this.store.leaveZone(open.org_id);
            void this.reloadOrganisation(open.org_id);
            return;
          }
        }
        this.leaveActive();
        return this.store.backToChats();
      // The rail (joana/shell/frame.ts). ONE door for every area rather than a
      // slot per existing command: a rail slot opens an area and nothing else
      // (Rail.dc.html admission rule 3), and routing them all through one case
      // is what lets `shell.test.ts` prove every slot lands somewhere.
      case "appearance_set":
        this.setAppearance(decodeTheme(String(payload.choice ?? "")));
        return;
      // G16 — the chat wallpaper. An id this build does not ship is IGNORED
      // rather than falling back to the default: the only way to send one is a
      // stale tree, and quietly resetting somebody's canvas is a worse answer
      // than doing nothing.
      case "wallpaper_set": {
        const picked = wallpaperById(String(payload.id ?? ""));
        if (picked) this.setWallpaper(picked);
        return;
      }
      case "rail_open":
        return this.openArea(String(payload.area ?? ""));
      case "set_draft":
        return this.onDraft(str(payload.value));
      case "send":
        return this.doSend();
      case "logout":
        return this.doLogout();
      case "open_profile":
        this.openProfile();
        return;
      // --- Status (D8, ADR-0023) ---
      case "status_open":
        void this.openStatusScreen();
        return;
      case "status_view":
        void this.viewStatus(String(payload.author_id ?? ""));
        return;
      case "status_close":
        this.store.closeStatus();
        return;
      case "status_step":
        this.stepStatus(Number(payload.delta ?? 1));
        return;
      case "status_draft":
        this.store.setStatusDraft({ body: String(payload.value ?? "") });
        return;
      case "status_colour":
        this.cycleStatusColour();
        return;
      case "status_post":
        void this.postStatus();
        return;
      case "status_like":
        void this.likeStatus(String(payload.status_id ?? ""), Boolean(payload.liked));
        return;
      case "status_delete":
        void this.deleteStatus(String(payload.status_id ?? ""));
        return;
      case "status_mute":
        void this.muteStatusAuthor(String(payload.author_id ?? ""));
        return;
      case "status_report_open":
        this.store.reportStatus(String(payload.status_id ?? "") || null);
        return;
      case "status_report_cancel":
        this.store.reportStatus(null);
        return;
      case "status_notice_clear":
        this.store.setStatusNotice(null);
        return;
      // ADR-0027 D2d — dismiss the send-failure bar over a thread. Nothing is
      // lost by dismissing it: the refused message's text is back in the
      // composer.
      case "send_error_clear":
        this.store.setError(null);
        return;
      case "status_report_send":
        void this.reportStatus(String(payload.status_id ?? ""), String(payload.reason ?? "spam"));
        return;
      case "status_policy":
        void this.setStatusPolicy(String(payload.policy ?? "contacts"));
        return;
      // --- Circle posts (D9, ADR-0024) ---
      case "post_draft":
        this.store.setPostDraft({ body: String(payload.value ?? "") });
        return;
      case "post_audience":
        this.store.setPostDraft({ audience: String(payload.audience ?? "contacts") });
        return;
      case "post_publish":
        void this.publishPost();
        return;
      case "post_like":
        void this.likePost(String(payload.post_id ?? ""), Boolean(payload.liked));
        return;
      case "post_delete":
        void this.deletePost(String(payload.post_id ?? ""));
        return;
      case "post_comment_open":
        this.store.setCommenting(String(payload.post_id ?? "") || null);
        return;
      case "post_comment_draft":
        this.store.setCommentDraft(String(payload.value ?? ""));
        return;
      case "post_comment_send":
        void this.sendPostComment(String(payload.post_id ?? ""));
        return;
      case "post_comment_delete":
        void this.deletePostComment(String(payload.post_id ?? ""), String(payload.comment_id ?? ""));
        return;
      // --- Channels (D6, ADR-0024) ---
      case "updates_segment":
        this.store.setUpdatesSegment(payload.segment === "world" ? "world" : "circle");
        return;
      case "explore_open":
        this.store.openExplore(payload.focus === true);
        void this.exploreChannels({ reset: true });
        return;
      case "explore_query":
        this.store.setExploreQuery(String(payload.value ?? ""));
        this.scheduleExplore();
        return;
      case "explore_kind": {
        const next = payload.kind == null ? null : String(payload.kind);
        this.store.setExploreKind(this.store.state.exploreKind === next ? null : next);
        void this.exploreChannels({ reset: true });
        return;
      }
      case "channel_open":
        void this.openChannel(String(payload.channel_id ?? ""));
        return;
      case "channel_close":
        this.store.closeChannel();
        return;
      case "channel_follow":
        void this.followChannel(String(payload.channel_id ?? ""));
        return;
      // --- the reading screen (ADR-0036) ---
      case "channel_pick":
        this.store.setChannelPicker(payload.post_id == null ? null : String(payload.post_id));
        return;
      case "channel_react":
        void this.reactToChannelPost(String(payload.post_id ?? ""), payload.emoji == null ? null : String(payload.emoji));
        return;
      case "channel_mute":
        void this.setChannelMuted(payload.muted === true);
        return;
      case "channel_more":
        void this.loadOlderChannelPosts();
        return;
      case "channel_play":
        this.store.playChannelPost(String(payload.post_id ?? ""));
        return;
      case "channel_media_open":
        this.store.setChannelLightbox(payload.post_id == null ? null : String(payload.post_id));
        return;
      case "channel_media_close":
        this.store.setChannelLightbox(null);
        return;
      case "channel_forward_open":
        this.store.setChannelNotice(null);
        this.store.setChannelForwarding(payload.post_id == null ? null : String(payload.post_id));
        return;
      case "channel_forward_close":
        this.store.setChannelForwarding(null);
        return;
      case "channel_forward":
        void this.forwardChannelPost(String(payload.post_id ?? ""), String(payload.chat_id ?? ""));
        return;
      // --- Mazungumzo (D12, ADR-0025) ---
      case "duniami_tab": {
        const tab = payload.tab === "mazungumzo" ? "mazungumzo" : "channels";
        this.store.setDuniamiTab(tab);
        if (tab === "mazungumzo") void this.loadMazungumzo();
        return;
      }
      case "mazungumzo_segment":
        this.store.setMazungumzoSegment(payload.following === true);
        return;
      case "mazungumzo_follow":
        void this.followPerson(String(payload.user_id ?? ""), true);
        return;
      case "mazungumzo_unfollow":
        void this.followPerson(String(payload.user_id ?? ""), false);
        return;
      // --- ADR-0029: Threads inside WaveTalk ---
      case "mazungumzo_repost":
        void this.repost(String(payload.post_id ?? ""), true);
        return;
      case "mazungumzo_unrepost":
        void this.repost(String(payload.post_id ?? ""), false);
        return;
      case "mazungumzo_save":
        void this.savePost(String(payload.post_id ?? ""), true);
        return;
      case "mazungumzo_unsave":
        void this.savePost(String(payload.post_id ?? ""), false);
        return;
      case "mazungumzo_quote":
        this.store.setQuoting(String(payload.post_id ?? ""));
        return;
      case "mazungumzo_cancel_quote":
        this.store.setQuoting(null);
        return;
      case "mazungumzo_thread":
        void this.openThread(String(payload.post_id ?? ""));
        return;
      case "mazungumzo_close_thread":
        this.store.setThread([]);
        return;
      case "mazungumzo_list":
        void this.openFeedList(
          payload.list === "saved" || payload.list === "liked" || payload.list === "activity"
            ? payload.list
            : null,
        );
        return;
      case "mazungumzo_activity_filter":
        void this.loadActivity(
          typeof payload.filter === "string" ? payload.filter : undefined,
        );
        return;
      case "mazungumzo_search":
        void this.searchMazungumzo(String(payload.q ?? ""));
        return;
      // --- Office (D6/D7, ADR-0024 Phase 3) ---
      case "office_open":
        // The caller names its own door: Profile's row and the World segment's
        // "Create a channel" both land here, and back has to return to the one
        // that was used.
        this.store.openOffice(
          this.store.state.screen === "updates" ? "updates" : "profile"
        );
        void this.loadOffice();
        return;
      case "office_close":
        this.store.closeOffice();
        return;
      case "office_create_open":
        this.store.openOfficeCreate();
        void this.loadOfficeReference();
        return;
      case "office_form": {
        const field = String(payload.field ?? "");
        const value = payload.value == null ? null : String(payload.value);
        this.setOfficeField(field, value);
        return;
      }
      case "office_create":
        void this.createPublisher();
        return;
      // --- ADR-0018 step 1: organisations and zones ---
      case "orgs_open":
        this.store.openOrganisations();
        void this.loadOrganisations();
        return;
      case "org_open":
        void this.openOrganisation(String(payload.org_id ?? ""));
        return;
      case "org_create_open":
        this.store.openOrganisationCreate();
        return;
      case "org_form":
        this.store.setOrgForm({ name: String(payload.value ?? "") });
        return;
      case "org_create":
        void this.createOrganisation();
        return;
      case "zone_create_open":
        this.store.openZoneCreate();
        return;
      case "zone_form": {
        const field = String(payload.field ?? "");
        const value = String(payload.value ?? "");
        this.store.setZoneForm(field === "kind" ? { kind: value } : { name: value });
        return;
      }
      case "zone_create":
        void this.createZone();
        return;
      // A zone IS a chat, so it opens the conversation — the same screen, the
      // same composer, the same everything. There is no zone screen.
      case "zone_open":
        void this.openChat(String(payload.chat_id ?? ""));
        return;
      case "org_set_role":
        void this.manageOrg("org_set_role", {
          user_id: String(payload.user_id ?? ""),
          role: String(payload.role ?? "member"),
        });
        return;
      case "org_remove_member":
        void this.manageOrg("org_remove_member", { user_id: String(payload.user_id ?? "") });
        return;
      case "org_leave":
        void this.leaveOrganisation();
        return;
      // ADR-0037 E — the assistant and API keys. One editor slot on the
      // organisation's screen; the server refuses a non-manager whatever opens.
      case "asst_name_open": {
        const assistant = this.store.state.assistantSetup?.assistant ?? null;
        const org = this.store.state.organisations.find((o) => o.id === this.store.state.activeOrgId);
        this.store.setOrgEditor({
          kind: "assistant_name",
          creating: assistant === null,
          value: assistant?.display_name ?? this.t("%s Assistant").replace("%s", org?.name ?? ""),
        });
        return;
      }
      case "asst_zone_open": {
        const zone = this.store.state.assistantSetup?.zones.find((z) => z.chat_id === payload.chat_id);
        if (!zone) return;
        this.store.setOrgEditor({
          kind: "zone_assistant",
          chat_id: zone.chat_id,
          zone_name: zone.name ?? "",
          enabled: zone.assistant_enabled,
          shape: zone.assistant_shape ?? "",
          instructions: zone.assistant_instructions ?? "",
        });
        return;
      }
      case "key_issue_open":
        this.store.setOrgEditor({ kind: "key_issue", name: "", read: true, post: true, days: 90 });
        return;
      case "key_revoke_open":
        this.store.setOrgEditor({
          kind: "key_revoke",
          key_id: String(payload.key_id ?? ""),
          name: String(payload.name ?? ""),
        });
        return;
      case "org_editor_field":
        this.store.patchOrgEditor({ [String(payload.field ?? "")]: payload.value });
        return;
      // Cancel, Done and Back all land here, and it is what drops an issued
      // key's secret: the editor slot is the only place it was held.
      case "org_editor_close":
        this.store.setOrgEditor(null);
        return;
      case "org_editor_save":
        void this.saveOrgEditor();
        return;
      case "key_copy":
        void this.copyIssuedKey();
        return;
      case "publisher_open":
        void this.openPublisher(String(payload.publisher_id ?? ""));
        return;
      case "publisher_publishing":
        void this.setPublishing(
          String(payload.publisher_id ?? ""),
          payload.enabled === true
        );
        return;
      case "publisher_draft":
        this.store.setOfficeDraft(String(payload.value ?? ""));
        return;
      case "publisher_publish":
        void this.publishToChannel(String(payload.publisher_id ?? ""));
        return;
      case "profile_rename":
        void this.renameSelf();
        return;
      // ADR-0028 — the interface language. `tag` absent means "follow my
      // browser", which CLEARS the setting rather than storing a name for
      // today's browser locale.
      case "language_set":
        void this.setUiLanguage(typeof payload.tag === "string" ? payload.tag : null);
        return;
      case "avatar_change_photo":
        void this.changeAvatarPhoto();
        return;
      case "avatar_pick_emoji":
        void this.pickEmojiAvatar();
        return;
      case "avatar_pick_sticker":
        void this.pickStickerAvatar();
        return;
      case "avatar_pick_frame":
        void this.pickFrame();
        return;
      case "avatar_history":
        void this.loadHistory();
        return;
      case "avatar_remove":
        void this.setAvatar(null);
        return;
      case "avatar_policy":
        void this.setPolicy(String(payload.policy ?? "everyone"));
        return;
      case "findability_set":
        void this.setFindability(String(payload.policy ?? ""));
        return;
      case "avatar_exclude":
        void this.addExclusion();
        return;
      case "avatar_include":
        void this.removeExclusion(String(payload.user_id ?? ""));
        return;
      case "avatar_restore":
        void this.avatarProfileCall(() => this.api.restoreAvatar(String(payload.version ?? "")));
        return;
      case "avatar_forget":
        void (async () => {
          try {
            const h = await this.api.forgetAvatar(String(payload.version ?? ""));
            this.store.setHistory(h.history);
          } catch (e) {
            this.store.setProfileError(errMsg(e));
          }
        })();
        return;
      case "open_apps":
        return this.loadApps();
      case "apps_tab":
        return this.switchAppsTab(payload.tab === "installed" ? "installed" : "featured");
      case "apps_search":
        return this.searchApps(str(payload.value));
      case "open_miniapp":
        return this.openMiniApp(str(payload.app_id), str(payload.title) || "Mini-app");
      case "close_miniapp":
        return this.closeMiniApp();
    }
  }

  // --- embedded mini-apps --------------------------------------------------

  private async loadApps(): Promise<void> {
    this.store.openApps();
    await this.loadTab("featured");
  }

  private async switchAppsTab(tab: "featured" | "installed"): Promise<void> {
    this.store.setAppsTab(tab);
    await this.loadTab(tab);
  }

  // Load the list backing a tab. Featured always offers the built-in sample first
  // (so the picker works even with an empty/unreachable registry); installed shows
  // only the user's installs. A registry failure degrades gracefully, never an
  // error wall. Guarded by a sequence token so a slow earlier load can't overwrite
  // a newer tab/search the user has since switched to.
  private async loadTab(tab: "featured" | "installed"): Promise<void> {
    const seq = ++this.appsSeq;
    try {
      const items =
        tab === "installed"
          ? await this.api.listInstalledMiniApps()
          : [BUILTIN_SAMPLE, ...(await this.api.listFeaturedMiniApps())];
      if (seq === this.appsSeq) this.store.setApps(items);
    } catch {
      if (seq === this.appsSeq) this.store.setApps(tab === "featured" ? [BUILTIN_SAMPLE] : []);
    }
  }

  // Reflect the keystroke immediately, but DEBOUNCE the registry hit: a fast
  // typist fires one search when they pause, not one per character. Clearing the
  // box (or any newer keystroke) cancels a pending search. The appsSeq guard
  // still protects against an in-flight response landing after a newer action.
  private searchApps(value: string): void {
    this.scheduler.clearTimeout(this.searchDebounceHandle);
    this.searchDebounceHandle = null;
    const query = value.trim();
    if (query === "") {
      // Clearing is immediate — reloading the current tab is local + cheap.
      this.store.clearAppsSearch();
      void this.loadTab(this.store.state.appsTab);
      return;
    }
    this.store.setAppsQuery(value); // input reflects the text now; fetch waits
    this.searchDebounceHandle = this.scheduler.setTimeout(() => {
      this.searchDebounceHandle = null;
      void this.runSearch(query);
    }, this.searchDebounceMs);
  }

  private async runSearch(query: string): Promise<void> {
    this.store.beginAppsSearch(query);
    const seq = ++this.appsSeq;
    try {
      const results = await this.api.searchMiniApps(query);
      if (seq === this.appsSeq) this.store.setApps(results);
    } catch {
      if (seq === this.appsSeq) this.store.setApps([]);
    }
  }

  private openMiniApp(appId: string, title: string): void {
    this.store.openMiniApp(title);
    this.opts.onOpenMiniApp?.(appId, title);
  }

  private closeMiniApp(): void {
    this.opts.onCloseMiniApp?.();
    this.store.closeMiniApp();
  }

  // --- auth: QR (scan with phone) -------------------------------------------

  /** Open a fresh QR login session and start the status poll loop. Any prior
   *  session/loop is invalidated first (refresh, mode switches, re-entry). */
  private async startQrLogin(): Promise<void> {
    const seq = this.stopQrLogin();
    this.store.setQr({ status: "loading", svg: null, scannedBy: null });
    try {
      // ADR-0027 D4.4 — the identity is generated BEFORE the code is rendered,
      // because the code has to carry it. A failure here must not take the
      // login with it: the browser then shows a plain QR, signs in, and is not
      // a device, which is what web has always done.
      let identity: { identityKey: string; registrationId: number } | undefined;
      if (this.signal) {
        try {
          identity = await this.signal.identity();
        } catch {
          identity = undefined;
        }
      }
      const created = await this.api.webLoginCreate(identity);
      if (seq !== this.qrSeq) return; // superseded while in flight
      this.qrSession = { sid: created.sid, secret: created.client_secret };
      this.store.setQr({ status: "waiting", svg: created.qr_svg, scannedBy: null });
      this.scheduleQrPoll(seq);
    } catch {
      if (seq === this.qrSeq) this.store.setQr({ status: "error", svg: null });
    }
  }

  /** Invalidate the current QR session + poll loop. Returns the new seq so a
   *  caller that immediately restarts can tag its own work. */
  private stopQrLogin(): number {
    this.scheduler.clearTimeout(this.qrPollHandle);
    this.qrPollHandle = null;
    this.qrSession = null;
    return ++this.qrSeq;
  }

  private scheduleQrPoll(seq: number): void {
    this.qrPollHandle = this.scheduler.setTimeout(() => {
      this.qrPollHandle = null;
      void this.pollQrLogin(seq);
    }, this.qrPollIntervalMs);
  }

  /**
   * Publish this browser as a Signal device, using the signature the account's
   * primary minted when it approved the code (ADR-0027 D4.4 step 5).
   *
   * Every exit that is not "published" leaves the browser signed in and not a
   * device — which is a real state with a real consequence (an encrypted chat
   * will not open here) and is recorded on the store so the UI can say it,
   * rather than swallowed into a shrug.
   */
  private async linkSignalDevice(login: LoginResponse & { signal_link?: SignalLinkCredential | null }): Promise<void> {
    const link = login.signal_link;
    if (!this.signal || !link) {
      this.store.setSignalDeviceLinked(false);
      return;
    }
    try {
      // The token is needed for the publish and the session is not adopted yet.
      // For the same reason the ACCOUNT ID travels as an argument: the store
      // learns who signed in only in `afterLoginAttempt`, one line below.
      this.api.setToken(login.access_token);
      await this.signal.adoptAndPublish(login.user.id, link.device_number, link.link_signature);
      this.store.setSignalDeviceLinked(true);
    } catch (err) {
      // Not a shrug: a browser that signed in and did not become a device
      // cannot open an encrypted chat, and the only person who can see why is
      // whoever is looking at this console.
      console.error("[wavetalk] this browser signed in but did not become a device:", err);
      this.store.setSignalDeviceLinked(false);
    }
  }

  private async pollQrLogin(seq: number): Promise<void> {
    const session = this.qrSession;
    if (seq !== this.qrSeq || !session) return;
    try {
      const res = await this.api.webLoginPoll(session.sid, session.secret);
      if (seq !== this.qrSeq) return;
      switch (res.status) {
        case "pending":
          this.scheduleQrPoll(seq);
          return;
        case "scanned":
          this.store.setQr({ status: "scanned", scannedBy: res.scanned_by ?? null });
          this.scheduleQrPoll(seq);
          return;
        case "approved":
          this.stopQrLogin();
          if (res.login) {
            // Register the device BEFORE the session is adopted. The publish
            // needs the access token, and `afterLoginAttempt` is what routes
            // away from the login screen — doing it after would race a repaint
            // against a network call, and the failure would look like a browser
            // that linked and then could not read anything.
            await this.linkSignalDevice(res.login);
            await this.afterLoginAttempt(res.login, true);
          }
          return;
        case "rejected":
          this.stopQrLogin();
          this.store.setQr({ status: "rejected" });
          return;
        case "expired":
          this.stopQrLogin();
          this.store.setQr({ status: "expired" });
          return;
      }
    } catch {
      if (seq !== this.qrSeq) return;
      this.stopQrLogin();
      this.store.setQr({ status: "error" });
    }
  }

  private switchLoginMode(mode: "qr" | "password"): void {
    if (mode === this.store.state.loginMode) return;
    this.store.setLoginMode(mode);
    if (mode === "qr") void this.startQrLogin();
    else this.stopQrLogin();
  }

  // --- auth: password --------------------------------------------------------

  private async doLogin(): Promise<void> {
    const { loginUsername, loginPassword } = this.store.state;
    if (!loginUsername || !loginPassword) {
      this.store.setError(this.t("Enter your username and password"));
      return;
    }
    this.store.setLoading(true);
    this.store.setError(null);
    try {
      const res = await this.api.login(loginUsername, loginPassword);
      await this.afterLoginAttempt(res);
    } catch (e) {
      this.store.setError(errMsg(e));
    }
  }

  private async doVerifyTwoFactor(): Promise<void> {
    const { twoFactorToken, twoFactorCode } = this.store.state;
    if (!twoFactorToken) return;
    this.store.setLoading(true);
    this.store.setError(null);
    try {
      const res = await this.api.verifyTwoFactor(twoFactorToken, twoFactorCode);
      await this.afterLoginAttempt(res);
    } catch (e) {
      this.store.setError(errMsg(e));
    }
  }

  /**
   * `deviceSettled` says the caller has ALREADY established whether this
   * browser is a device for this sign-in — which the QR path has, because it
   * just tried to publish one and knows whether that worked. Asking the vault
   * again there would overwrite a known failure with a stale success: a publish
   * the directory refused leaves a device record behind, and "there is a record"
   * is not "the account knows about it".
   */
  private async afterLoginAttempt(res: LoginResult, deviceSettled = false): Promise<void> {
    if (isTwoFactor(res)) {
      this.store.needTwoFactor(res.challenge_token);
      return;
    }
    // A parallel QR session (e.g. password login while the code was up) is
    // dead weight now — stop the loop before entering the app.
    this.stopQrLogin();
    this.refreshToken = res.refresh_token;
    this.opts.onRefreshToken?.(res.refresh_token);
    // The client needs it too, not just storage: it is what lets a 401 be
    // answered with a rotation instead of a sign-out.
    this.api.setRefreshToken(res.refresh_token);
    this.api.setToken(res.access_token);
    this.store.loginSuccess(res.user);
    // ADR-0027 D4.6 — ask the vault whether this browser is a device, for the
    // account that just signed in. `linkSignalDevice` sets the same flag on the
    // QR path, but only on the page that did the linking: every other way in
    // here (a password sign-in on a browser linked yesterday, and a restored
    // session once one exists) would otherwise report a linked device as
    // unlinked, and the drain below would have no id to read its inbox under.
    if (this.signal && !deviceSettled) {
      try {
        this.store.setSignalDeviceLinked(await this.signal.attach());
      } catch {
        this.store.setSignalDeviceLinked(false);
      }
    }
    this.connectSocket();
    await this.loadChats();
    await this.askForNameIfMissing(res.user);
  }

  /**
   * ADR-0030 D2 — an account made before a name was required.
   *
   * The browser is a LINKED device, so the account was created on a phone and
   * the phone asks too; this is here because "web has no excuse" is a rule in
   * this project, and because somebody who signs in here first should not have
   * to go and find their phone to become findable.
   *
   * A prompt, never a block: a cancelled prompt simply returns next sign-in.
   */
  private async askForNameIfMissing(user: User): Promise<void> {
    if (user.display_name && user.display_name.trim() !== "") return;
    const ask = this.opts.askText;
    if (!ask) return;
    const name = await ask(
      this.t("Right now the only way anyone can find you is by typing @%s exactly. Add the name people actually call you and they will find you by that instead.", user.username ?? ""),
      "",
    );
    if (!name || name.trim() === "") return;
    try {
      const { user: updated } = await this.api.updateProfile({ display_name: name.trim() });
      this.store.loginSuccess(updated);
    } catch (e) {
      this.store.setError(errMsg(e));
    }
  }

  private async loadChats(): Promise<void> {
    try {
      const chats = await this.api.listChats();
      this.store.setChats(await this.withLocalPreviews(chats));
    } catch (e) {
      this.store.setError(errMsg(e));
    }
  }

  /**
   * ADR-0034 D1 — put this browser's own decrypted preview back on a sealed
   * conversation's row.
   *
   * `GET /chats` answers with a content-free STUB for an encrypted chat's
   * newest message: a shape, a sender, a timestamp, and no words, because the
   * server holds none. Left alone the row would read "Message" even here,
   * where the message HAS been decrypted and is sitting in the cache — and
   * before the stub existed it read "No messages yet", which is worse.
   *
   * The comparison is `>=` and it is what makes a delete-for-everyone still
   * win: the server tombstones the stub, its timestamp stays the newest, and
   * the older local message correctly does not replace it.
   *
   * Best-effort throughout. A browser that is not a device, or one whose cache
   * will not open, gets the server's rows unchanged — which is a real state
   * and not an error.
   */
  private async withLocalPreviews(chats: Chat[]): Promise<Chat[]> {
    if (!this.signal) return chats;
    return await Promise.all(
      chats.map(async (chat) => {
        // Keyed on the CONVERSATION, not on the row being a stub, and the
        // difference shows in a mixed-build chat: a message sent from a build
        // older than ADR-0034 D1 leaves no stub at all, so the server's newest
        // row is something else entirely and this browser's decrypted copy is
        // still the truer preview. The `>=` below is what decides.
        if (chat.e2e !== true) return chat;
        const local = (await this.decryptedMessages(chat.id)).at(-1);
        if (!local) return chat;
        const at = Date.parse(local.created_at ?? "");
        const stubAt = Date.parse(chat.last_message?.created_at ?? "");
        if (Number.isNaN(at) || (!Number.isNaN(stubAt) && at < stubAt)) return chat;
        return {
          ...chat,
          last_message: {
            ...(chat.last_message ?? { id: local.id, message_type: "text" }),
            body: local.body,
            message_type: local.message_type,
            user_id: local.user_id,
            created_at: local.created_at,
            // The row has words now, so it must stop saying "Message".
            e2e: false,
          },
        };
      }),
    );
  }

  private async doLogout(): Promise<void> {
    this.leaveActive();
    this.socket?.disconnect();
    this.socket = null;
    // It belonged to that socket: kept, the next session would push on it
    // (a status, a post, the inbox drain) before its own socket opened.
    this.userChannel = null;
    try {
      await this.api.logout(this.refreshToken ?? undefined);
    } catch {
      // best-effort; the local session is cleared regardless
    }
    this.api.setToken(null);
    this.api.setRefreshToken(null);
    this.refreshToken = null;
    this.opts.onRefreshToken?.(null);
    // Reset to a fresh login screen.
    this.store.setChats([]);
    this.store.backToChats();
    this.store.setConnection("offline");
    this.store.loginReset();
    // Back on the login screen — put a fresh scan code up immediately.
    void this.startQrLogin();
  }

  // --- realtime ------------------------------------------------------------

  private connectSocket(): void {
    // The socket mints a FRESH single-use ticket per (re)connect via REST. The
    // ApiClient holds the access token (just set), so it rides the Bearer header
    // of the ticket call — never the socket URL.
    this.socket = this.opts.createSocket(() =>
      // ADR-0033 D9 rides along with the ticket: the socket cannot carry a
      // header from a browser at all, and `connect/3` reads params on every
      // transport there is. The two least secret things this client knows.
      this.api.realtimeTicket().then((t) => ({
        ticket: t.ticket,
        ...(this.opts.clientBuild
          ? {
              platform: this.opts.clientBuild.platform,
              build: String(this.opts.clientBuild.build),
            }
          : {}),
      })),
    );
    // s179 — a socket that opens AGAIN (a reconnect after the laptop slept,
    // a network change) refetches the chat list. Being removed from a group
    // reaches a client only as a LIVE `chat_updated` frame, and a frame sent
    // while this socket was down is gone; the phones reconcile on coming back
    // to the foreground, and this is the browser's version of that moment.
    // Not on the FIRST open: `loadChats` has just run.
    let openedBefore = false;
    this.socket.onOpen(() => {
      this.store.setConnection("online");
      this.joinUserChannel();
      if (openedBefore) this.scheduleChatRefresh();
      openedBefore = true;
    });
    this.socket.onClose(() => {
      // After auth a drop is a reconnect-in-progress, not a logout.
      this.store.setConnection(this.store.state.me ? "connecting" : "offline");
    });
    this.store.setConnection("connecting");
    this.socket.connect();
  }

  private async openChat(chatId: string): Promise<void> {
    if (!chatId) return;
    this.store.openChat(chatId);
    this.joinChannel(chatId);
    // A zone this browser has no row for (s185) — ask for it, so the header
    // names it. Not awaited: the thread must not wait on its title. A 404 (not
    // a zone) leaves the fallback name standing. Below the zone's floor the
    // row still comes, without its last message (s186) — the history read
    // below is what answers the floor.
    if (!chatById(this.store.state, chatId)) {
      this.api.zone(chatId).then((r) => this.store.rememberZoneRow(r.chat), () => {});
    }
    try {
      // ADR-0027 D4.6 — REST answers with the PLAINTEXT rows and nothing else,
      // because an E2E message has no server row to answer with. The encrypted
      // half of this conversation lives only in this browser, and the two are
      // shown as one list because to the reader they ARE one conversation.
      //
      // In PARALLEL, and MERGED rather than set: opening the cache can mean
      // opening a database, and every millisecond added to this path is a
      // millisecond in which a person can send a message that a replacing
      // history would then wipe off their screen.
      const [msgs, decrypted] = await Promise.all([
        this.api.listMessages(chatId, { limit: 50 }),
        this.decryptedMessages(chatId),
      ]);
      if (this.store.state.activeChatId !== chatId) return;
      this.store.mergeMessages([...msgs, ...decrypted]);
      this.store.setZoneUpdateRequired(chatId, false);
    } catch (e) {
      // ADR-0037 B — not an error bar: the pane itself becomes the answer.
      if (isZoneUpdateRequired(e)) this.store.setZoneUpdateRequired(chatId, true);
      else this.store.setError(errMsg(e));
    }
    try {
      await this.api.markChatRead(chatId);
      this.store.markChatReadLocal(chatId);
    } catch {
      // non-fatal
    }
    // ADR-0007 — is this conversation end-to-end encrypted? Asked LAST and
    // separately, because the answer is what the header says about the chat
    // and not what the chat needs in order to open: a directory that is slow
    // must not hold up the messages.
    //
    // A failure leaves the state at `null`, which draws NOTHING. The
    // alternative — assuming "not encrypted" when we could not ask — is a
    // screen telling somebody their messages are readable on the strength of a
    // request that never arrived.
    try {
      const { e2e } = await this.api.chatE2EState(chatId);
      this.store.setChatE2E(chatId, e2e === true);
    } catch {
      /* unknown stays unknown */
    }
  }

  private joinChannel(chatId: string): void {
    if (!this.socket) return;
    this.leaveActive();
    const chan = this.socket.channel(`chat:${chatId}`, {});

    chan.on("message_history", (p) => {
      const messages = (p as { messages?: Message[] }).messages ?? [];
      // Merged, not set, for the same reason the REST fetch above is: this
      // frame arrives whenever the server sends it, and replacing the list with
      // it deletes anything sent in the meantime.
      this.store.mergeMessages(messages);
    });
    chan.on("new_message", (m) => this.store.upsertMessage(normalize(m)));
    chan.on("user_typing", (p) => {
      const { user_id, is_typing } = p as { user_id: string; is_typing: boolean };
      if (user_id && user_id !== this.store.state.me?.id) this.store.setTyping(user_id, !!is_typing);
    });
    chan.on("message_read", () => {
      // Read receipts could update per-message ticks; the list-level badge is
      // already cleared on open. Hook reserved for delivery/▾read state.
    });

    // ADR-0027 D4.6 — the broadcast is a DOORBELL, not a delivery. It carries
    // one envelope per recipient device and this browser could pick its own out
    // of the batch, but it would then be reading the fan-out shape in one place
    // and the inbox shape in another, and only the inbox carries the sender's
    // device NUMBER — which is half the address a decrypt needs. So the ring
    // triggers a drain and the drain is the only reader. The phones made the
    // same call, for the same reason.
    chan.on("new_encrypted_message", () => void this.drainEncryptedInbox());

    // ADR-0037 B — admitted, then shown out: the zone's floor rose under this
    // open channel. Same payload as a refused join, and a `phx_close` follows,
    // which already stops this channel rejoining on a timer.
    chan.on("refused", (r) => {
      if ((r as { reason?: unknown } | null)?.reason === "zone_update_required") {
        this.store.setZoneUpdateRequired(chatId, true);
      }
    });

    chan
      .join()
      .receive("ok", () => this.store.setZoneUpdateRequired(chatId, false))
      .receive("error", (r) => {
        // ADR-0037 B. The channel would otherwise rejoin on a timer for as
        // long as the tab is open, against a refusal that stands until this
        // bundle is replaced — so it is left, and the pane says why.
        if ((r as { reason?: unknown } | null)?.reason !== "zone_update_required") return;
        this.store.setZoneUpdateRequired(chatId, true);
        chan.leave();
      });
    this.activeChannel = chan;
    this.typingSent = false;
    // The catch-up. Everything sent while this browser was closed is sitting in
    // its inbox, and the inbox is DEVICE-scoped, not chat-scoped: opening any
    // one chat collects the backlog for all of them, and each message routes to
    // its own chat on the way in.
    void this.drainEncryptedInbox();
  }

  // --- E2E: this browser reads its own inbox (ADR-0027 D4.6) ----------------

  /**
   * Pull whatever ciphertext is addressed to this device, over the chat channel
   * we are already on.
   *
   * The pushes are the ones the phones have used since E2E shipped —
   * `fetch_encrypted_inbox` and `ack_encrypted` — and no new mechanism was
   * added for web. The one thing worth stating: this is best-effort by design.
   * A drain that fails leaves every envelope in the inbox, so the next
   * doorbell or the next chat opened tries again, and nothing is lost by
   * saying nothing here.
   */
  private async drainEncryptedInbox(): Promise<void> {
    if (!this.signal) return;
    // The USER topic, always, and never the chat one.
    //
    // The inbox is DEVICE-scoped: which conversation is open has nothing to do
    // with what is addressed to this browser. Reading it on `chat:<id>` worked
    // only because something was always open — and a history bundle, which
    // arrives for chats nobody has open, is exactly the case that was not.
    // One transport, several triggers.
    const chan = this.userChannel;
    if (!chan) return;
    const transport: InboxTransport = {
      fetch: async (deviceRowId) => {
        const reply = (await chan.push("fetch_encrypted_inbox", { device_id: deviceRowId }).promise()) as {
          messages?: unknown[];
        } | null;
        return (reply?.messages ?? []) as never;
      },
      ack: async (deviceRowId, messageIds) => {
        await chan.push("ack_encrypted", { device_id: deviceRowId, message_ids: messageIds }).promise();
      },
    };
    try {
      await this.signal.drain(
        transport,
        (m) => this.store.upsertMessage(toMessage(m)),
        // ADR-0027 D4.7 — "Syncing your chats…", with a real number behind it.
        // An empty conversation list while a phone is busy sending one is a lie
        // about state, and the one thing a person in that moment needs told.
        (seq, total) => this.noteHistoryProgress(seq, total),
      );
    } catch {
      // Left unacked and un-narrated on purpose — see the doc above.
    }
  }

  /**
   * A history frame landed (ADR-0027 D4.7) — record it, and arm the thing that
   * takes the sentence back down.
   *
   * The bundle has no end frame and deliberately none: a phone that stops half
   * way sends no "I gave up", so a receiver waiting for one would say "Syncing
   * your chats…" until the tab was closed. What ends it is either the last
   * frame (`seq === total`, handled in the store) or SILENCE — and silence is
   * only observable with a clock.
   */
  private noteHistoryProgress(seq: number, total: number): void {
    this.store.setHistorySync(seq, total);
    this.scheduler.clearTimeout(this.historySyncStall);
    if (this.store.state.historySync === null) return;
    this.historySyncStall = this.scheduler.setTimeout(
      () => this.store.clearHistorySync(),
      HISTORY_SYNC_STALL_MS,
    );
  }

  private historySyncStall: unknown = null;

  /** What this browser has already decrypted for one chat, in the shape the
   *  renderer draws. Empty for a browser that is not a device, which is the
   *  ordinary case and not an error. */
  private async decryptedMessages(chatId: string): Promise<Message[]> {
    if (!this.signal) return [];
    try {
      return (await this.signal.readable(chatId)).map(toMessage);
    } catch {
      return [];
    }
  }

  // --- profile / avatars (ADR-0022) ---------------------------------------

  /**
   * Open a rail area.
   *
   * Deliberately reuses the SAME entry points the in-screen controls use
   * rather than setting `screen` itself — each of those does more than change
   * a screen (Explore resets its query, Apps fetches the registry, Updates
   * loads the tray), and a rail that navigated by assignment would land on
   * screens holding the last area's data.
   */
  /**
   * Choose an appearance, and remember it in this browser.
   *
   * The DOM write is the HOST's, through `onAppearance` — the shell owns no
   * document. That seam is also what lets the Node tests assert the choice
   * without a browser: they pass a recorder and read what it was told.
   */
  private setAppearance(choice: ThemeChoice): void {
    if (choice === this.store.state.appearance) return;
    this.applyAppearance(choice);
    try {
      if (choice === "system") localStorage.removeItem(THEME_KEY);
      else localStorage.setItem(THEME_KEY, choice);
    } catch {
      // Storage blocked: the choice holds for this session and is forgotten
      // on reload, which is the honest outcome and not an error to report.
    }
  }

  private applyAppearance(choice: ThemeChoice): void {
    this.store.setAppearance(choice);
    this.opts.onAppearance?.(choice);
  }

  /**
   * G16 — choose what the conversation is drawn on.
   *
   * Same shape as `setAppearance` down to the storage key's neighbour, because
   * it is the same kind of setting: this browser's, remembered here, and
   * carried to the page as CSS rather than as tree — see `wallpaperCss` for
   * why a hex in the tree would be the light colour forever.
   */
  private setWallpaper(w: ChatWallpaper): void {
    if (w.id === this.store.state.wallpaper.id) return;
    this.applyWallpaper(w);
    try {
      localStorage.setItem(WALLPAPER_KEY, w.id);
    } catch {
      // Storage blocked: the choice holds for this session and is forgotten on
      // reload, which is the honest outcome and not an error to report.
    }
  }

  private applyWallpaper(w: ChatWallpaper): void {
    this.store.setWallpaper(w);
    this.opts.onWallpaper?.(wallpaperCss(w));
  }

  private async openArea(area: string): Promise<void> {
    switch (area) {
      case "chats":
        // `showChats`, not `backToChats`: the thread stays in the detail pane,
        // exactly as it does when the rail opens Updates or Office. Closing it
        // here would also have left the socket subscribed to a chat topic with
        // nothing on screen reading it, because the rail deliberately does not
        // leave the channel.
        this.store.showChats();
        return;
      case "updates":
        this.store.setUpdatesSegment("circle");
        return this.openStatusScreen();
      case "discover":
        this.store.openExplore(false);
        await this.exploreChannels({ reset: true });
        return;
      case "office":
        // "chats" and never the current screen: `officeReturn` is where BACK
        // lands, and a rail can be tapped from anywhere — including from
        // Office itself, which would make back a loop.
        this.store.openOffice("chats");
        return this.loadOffice();
      case "apps":
        return this.loadApps();
      case "me":
        this.openProfile();
        return;
    }
  }

  private openProfile(): void {
    this.store.openProfile();
    void (async () => {
      try {
        const [{ user }, privacy, history] = await Promise.all([
          this.api.profile(),
          this.api.avatarPrivacy(),
          this.api.avatarHistory(),
        ]);
        this.store.setProfile(user);
        this.store.adoptAccountLanguage(user);
        this.store.setPrivacy(privacy);
        this.store.setHistory(history.history);
      } catch (e) {
        this.store.setProfileError(errMsg(e));
      }
      // ADR-0031 D3 — the findability setting, in its own try for the same
      // reason as the QR below: a server that predates the surface answers
      // 422, and losing the whole Profile screen over a setting it does not
      // have yet is a worse answer than drawing the screen without the row.
      try {
        this.store.setFindability(await this.api.privacy("findability"));
      } catch {
        this.store.setFindability(null);
      }
      // E5 — separately, and deliberately not inside the Promise.all above: a
      // server too old to know this route must not take the whole Profile
      // screen down with it.
      try {
        const qr = await this.api.myContactQr();
        this.store.setContactQr(qr.payload, qr.qr_svg ?? null);
      } catch {
        // No code drawn. Every other row on this screen still works.
      }
    })();
  }

  /**
   * B9 — a tapped contact card resolves the CURRENT person.
   *
   * The bubble keeps the name the sender saw (a message is a record of what
   * somebody said); this is what that name means NOW. A 404 is rendered as
   * "Not on WaveTalk" without distinguishing nobody / inactive / hidden —
   * ADR-0031 D3's rule applies to every lookup, not only to discovery.
   */
  private openContactCard(userId: string): void {
    if (!userId) return;
    this.store.setContactCard({ userId, loading: true, notFound: false, user: null });
    void (async () => {
      try {
        const { user } = await this.api.getUser(userId);
        // A second tap while the first was in flight wins; this one is stale.
        if (this.store.state.contactCard?.userId !== userId) return;
        this.store.setContactCard({ userId, loading: false, notFound: false, user });
      } catch {
        if (this.store.state.contactCard?.userId !== userId) return;
        this.store.setContactCard({ userId, loading: false, notFound: true, user: null });
      }
    })();
  }

  /**
   * ADR-0007 — compute this browser's safety number for the open chat's peer.
   *
   * Direct chats only, and that is the protocol's limit rather than a scoping
   * decision: a fingerprint is between two identities, and a group has no
   * single "other person" to be one of them. The phones have the same limit —
   * `E2eService.peerOf` resolves the one member who is not you and answers
   * null for anything else.
   *
   * The peer is read from the chat's cached counterparts rather than fetched.
   * `GET /chats` already carries them, bounded and viewer-excluded, and a
   * direct chat has exactly one.
   */
  private openSafetyNumber(): void {
    const chatId = this.store.state.activeChatId;
    if (!chatId || !this.signal) return;
    const chat = chatById(this.store.state, chatId);
    const peer = (chat?.members ?? []).length === 1 ? chat!.members![0] : null;
    if (!peer) return;
    const peerLabel = peer.display_name || peer.username || "";

    this.store.setSafetyNumber({ chatId, peerLabel, loading: true, digits: null, peerDevice: null });
    void (async () => {
      let found: { digits: string; peerDevice: number } | null = null;
      try {
        found = await this.signal!.safetyNumber(peer.user_id);
      } catch {
        // Nothing to compare. Rendered as such — see `safetyNumberOverlay`,
        // which does not distinguish a failure from an absence, because
        // neither of them means somebody is in the middle.
        found = null;
      }
      // A different chat was opened while this was in flight. Its number is
      // not this conversation's, and drawing it would be the one mistake this
      // screen must never make.
      const open = this.store.state.safetyNumber;
      if (!open || open.chatId !== chatId) return;
      this.store.setSafetyNumber({
        chatId, peerLabel, loading: false,
        digits: found?.digits ?? null,
        peerDevice: found?.peerDevice ?? null,
      });
    })();
  }

  /** B9 — "Message" on the card: a direct chat with the person it names. */
  private async messageFromContactCard(userId: string): Promise<void> {
    if (!userId) return;
    try {
      const chat = await this.api.createDirectChat(userId);
      this.store.setContactCard(null);
      this.store.upsertChat(chat);
      await this.openChat(chat.id);
    } catch (e) {
      this.store.setError(errMsg(e));
    }
  }

  /** Every avatar mutation answers `{user}` — one funnel keeps them honest. */
  private async avatarProfileCall(call: () => Promise<{ user: User }>): Promise<void> {
    this.store.setProfileBusy(true);
    try {
      const { user } = await call();
      this.store.setProfile(user);
      this.store.setProfileError(null);
      const h = await this.api.avatarHistory();
      this.store.setHistory(h.history);
      // The header face reads state.me.
      if (this.store.state.me && user.id === this.store.state.me.id) this.store.loginSuccess(user);
    } catch (e) {
      this.store.setProfileError(errMsg(e));
    } finally {
      this.store.setProfileBusy(false);
    }
  }

  /**
   * Choose the language WaveTalk is drawn in (ADR-0028).
   *
   * The local copy is written FIRST and the page redraws immediately —
   * changing the language of the app in front of you must not wait on a
   * request, which is not a hypothetical requirement in this market. The
   * account write follows; a failure leaves the choice standing on this
   * browser, because reverting it would be the app undoing something the
   * person just did in order to report a network problem.
   */
  private async setUiLanguage(tag: string | null): Promise<void> {
    if (tag === this.store.state.uiLanguage) return;
    this.store.setUiLanguage(tag);
    try {
      if (tag === null) localStorage.removeItem(LANGUAGE_KEY);
      else localStorage.setItem(LANGUAGE_KEY, tag);
    } catch {
      // A browser with storage blocked still gets the language for this
      // session; the account is the durable home either way.
    }
    try {
      // "" CLEARS the column; an absent key would leave it alone, which is a
      // different request.
      await this.api.updateProfile({ ui_language: tag ?? "" });
    } catch {
      this.store.setProfileError(this.t("Saved in this browser. We will sync it to your account when you are back online."));
    }
  }

  private async renameSelf(): Promise<void> {
    const ask = this.opts.askText;
    if (!ask) return;
    const current = this.store.state.profile?.display_name ?? "";
    const name = await ask(this.t("Your name"), current);
    if (name === null || !name.trim()) return;
    await this.avatarProfileCall(() => this.api.updateProfile({ display_name: name.trim() }));
  }

  private async changeAvatarPhoto(): Promise<void> {
    const picker = this.opts.pickAvatarPhoto;
    if (!picker) return;
    const policy = this.store.state.privacy?.policy ?? "everyone";
    const picked = await picker({ policy });
    if (!picked) return;
    this.store.setProfileBusy(true);
    try {
      const { version } = await this.api.uploadAvatar(picked.result.full, picked.result.thumb);
      // Consent first, then the face (the ADR's ordering).
      const except = this.store.state.privacy?.except.map((e) => e.user_id) ?? [];
      this.store.setPrivacy(await this.api.setAvatarPrivacy(picked.policy, except));
      await this.avatarProfileCall(() =>
        this.api.updateProfile({ avatar: { kind: "photo", version } }),
      );
    } catch (e) {
      this.store.setProfileError(errMsg(e));
    } finally {
      this.store.setProfileBusy(false);
    }
  }

  private async setAvatar(avatar: { kind: string; ref?: string; value?: string } | null): Promise<void> {
    await this.avatarProfileCall(() => this.api.updateProfile({ avatar }));
  }

  private async pickEmojiAvatar(): Promise<void> {
    const pick = this.opts.pickFromGrid;
    if (!pick) return;
    const emoji = "😀 😁 😂 🤣 😊 😍 😎 🤩 😜 🤗 🤔 😴 😇 🥳 😭 😅 🙌 👍 ✊ 🙏 💪 👑 🎉 🔥 ⭐ ❤️ 💙 💚 💛 🧡 💜 🖤 🦁 🐘 🦒 🐆 🦓 🐊 🦅 🐠 🌍 🌙 ☀️ 🌈 ⚽ 🏀 🎧 🎸 📿 🕌 ⛪ 🌴 🥭 🍍 ☕".split(" ");
    const key = await pick(this.t("Pick an emoji"), emoji.map((e) => ({ key: e, emoji: e })));
    if (key) await this.setAvatar({ kind: "emoji", value: key });
  }

  private async pickStickerAvatar(): Promise<void> {
    const pick = this.opts.pickFromGrid;
    if (!pick) return;
    // The bundled pack, served same-origin (gen-stickers.sh writes it here).
    let refs: { ref: string; word?: string }[] = [];
    try {
      const res = await fetch("/stickers/wavetalk-classic/pack.json");
      const pack = (await res.json()) as { stickers?: { id: string; word?: string }[] };
      refs = (pack.stickers ?? []).map((s) => ({ ref: `wavetalk-classic/${s.id}`, word: s.word }));
    } catch {
      this.store.setProfileError(this.t("Couldn't load the sticker pack."));
      return;
    }
    const key = await pick(
      this.t("Pick a sticker"),
      refs.map((s) => ({ key: s.ref, imageUrl: `/stickers/${s.ref}.webp`, label: s.word })),
    );
    if (key) await this.setAvatar({ kind: "sticker", ref: key });
  }

  private async pickFrame(): Promise<void> {
    const pick = this.opts.pickFromGrid;
    if (!pick) return;
    let frames: { id: string; name: string }[] = [];
    try {
      const res = await fetch("/avatar-frames/frames.json");
      frames = ((await res.json()) as { frames: { id: string; name: string }[] }).frames;
    } catch {
      this.store.setProfileError(this.t("Couldn't load the frames."));
      return;
    }
    // ADR-0028 — the picker's own words, in the account's language. The store
    // already holds the choice; `resolveLanguage` turns it into what we draw.
    const key = await pick(this.t("Frame"), [
      { key: "__none__", label: this.t("None") },
      ...frames.map((f) => ({ key: f.id, imageUrl: `/avatar-frames/${f.id}.png`, label: f.name })),
    ]);
    if (key === null) return;
    await this.avatarProfileCall(() =>
      this.api.updateProfile({ frame: key === "__none__" ? null : key }),
    );
  }

  private async loadHistory(): Promise<void> {
    try {
      const h = await this.api.avatarHistory();
      this.store.setHistory(h.history);
    } catch (e) {
      this.store.setProfileError(errMsg(e));
    }
  }

  private async setPolicy(policy: string): Promise<void> {
    const except = this.store.state.privacy?.except.map((e) => e.user_id) ?? [];
    try {
      this.store.setPrivacy(await this.api.setAvatarPrivacy(policy, except));
      this.opts.avatars?.forgetMisses();
    } catch (e) {
      this.store.setProfileError(errMsg(e));
    }
  }

  /**
   * ADR-0031 D3. Optimistic like the phones, and it reverts to the value the
   * server last confirmed rather than to a guess — turning findability off is
   * a setting somebody checks once and then trusts, so a row left where they
   * put it after a refused write is a lie about who can reach them.
   */
  private async setFindability(policy: string): Promise<void> {
    const current = this.store.state.findability;
    if (!current || !policy || current.policy === policy) return;
    this.store.setFindability({ ...current, policy });
    try {
      this.store.setFindability(await this.api.setPrivacy("findability", policy));
    } catch (e) {
      this.store.setFindability(current);
      this.store.setProfileError(errMsg(e));
    }
  }

  private async addExclusion(): Promise<void> {
    const ask = this.opts.askText;
    if (!ask) return;
    const query = await ask(this.t("Hide your photo from… (name, @username, or phone)"), "");
    if (!query || query.trim().length < 2) return;
    try {
      const { users } = await this.api.searchUsers(query.trim());
      const pick = this.opts.pickFromGrid;
      if (!pick || users.length === 0) return;
        const chosen = await pick(
        this.t("Hide your photo from…"),
        users.map((u) => ({ key: u.id, label: personLabel(u, (k, ...a) => this.t(k, ...a)) })),
      );
      if (!chosen) return;
      const current = this.store.state.privacy;
      const except = Array.from(new Set([...(current?.except.map((e) => e.user_id) ?? []), chosen]));
      this.store.setPrivacy(await this.api.setAvatarPrivacy(current?.policy ?? "everyone", except));
    } catch (e) {
      this.store.setProfileError(errMsg(e));
    }
  }

  private async removeExclusion(userId: string): Promise<void> {
    const current = this.store.state.privacy;
    if (!current || !userId) return;
    try {
      this.store.setPrivacy(await this.api.setAvatarPrivacy(
        current.policy,
        current.except.map((e) => e.user_id).filter((id) => id !== userId),
      ));
      this.opts.avatars?.forgetMisses();
    } catch (e) {
      this.store.setProfileError(errMsg(e));
    }
  }

  // --- the user topic: group info changes reach a browser too --------------

  /** Joined once after connect: `chat_updated` (a rename, an icon, a policy)
   *  must reach a browser whose chat list is open. The broadcast is
   *  deliberately name-only (no faces — one payload to many), so the honest
   *  move is a viewer-scoped refetch, debounced. */
  private joinUserChannel(): void {
    const me = this.store.state.me;
    if (!this.socket || !me) return;
    const chan = this.socket.channel(`user:${me.id}`, {});
    // A reopen hands back the SAME channel, which the socket has already
    // rejoined: binding again would add one more copy of every handler per
    // reconnect. Only the catch-up is per open.
    if (chan === this.userChannel) {
      void this.drainEncryptedInbox();
      return;
    }
    chan.on("chat_updated", () => this.scheduleChatRefresh());

    // D8: the doorbell carries IDS and no content — one payload goes to many
    // and a status is not visible to all of them alike — so what it triggers
    // is a refresh of this viewer's own tray.
    chan.on("status_posted", () => void this.refreshStatuses());

    // …and the ask-on-join answer, which arrives before any doorbell could:
    // a status posted while this browser was closed is never re-broadcast to
    // it, because a doorbell only rings for whoever is listening.
    chan.on("status_tray", (payload: unknown) => {
      // `on` hands every listener `unknown`, because a frame is whatever the
      // server sent. Narrowing in the PARAMETER is a claim the runtime never
      // checks; narrowing here is one that survives a frame without a tray.
      const tray = (payload as { tray?: unknown[] } | null)?.tray ?? [];
      this.store.setStatusTray(tray as never);
      this.sweepStatusMedia();
    });

    // ADR-0027 D4.6/D4.7 — ciphertext addressed to THIS device exists.
    //
    // The chat topic's `new_encrypted_message` is the doorbell for a
    // conversation somebody is looking at. This is the doorbell for every other
    // one: a message sent from your own phone into a chat this browser does not
    // have open, and every frame of a history bundle, which covers chats
    // nobody has open by definition. Both carry nothing but "go and look" —
    // the inbox is the only thing that carries an envelope.
    chan.on("encrypted_inbox_ready", () => void this.drainEncryptedInbox());

    chan.join();
    this.userChannel = chan;
    // The catch-up, on the one topic that is always joined. Everything that
    // arrived while this browser was closed is sitting in its device inbox,
    // and until this it was only reachable by opening a conversation.
    void this.drainEncryptedInbox();
  }

  /** The `user:<id>` topic. Joined on every socket open and held for as long
   *  as the session lives — which is why the encrypted inbox is read on it
   *  (ADR-0027 D4.7) and not on whichever conversation happens to be open. */
  private userChannel: Channel | null = null;

  // --- Status (D8, ADR-0023) ------------------------------------------------

  private async openStatusScreen(): Promise<void> {
    this.store.openStatus();
    await this.refreshStatuses();
    await this.refreshFeed();
    await this.refreshChannels();
    try {
      this.store.setStatusAudience(await this.api.privacy("status"));
    } catch {
      // The screen still works without it; the picker says "Loading…".
    }
  }

  /** The tray has ONE home. Everything that shows a status reads the store. */
  private async refreshStatuses(): Promise<void> {
    try {
      const [tray, own] = await Promise.all([this.api.statusTray(), this.api.ownStatuses()]);
      this.store.setStatusTray(tray.tray);
      this.store.setStatusOwn(own.statuses);
      this.sweepStatusMedia();
    } catch {
      // A tray that fails to load is an empty tray, not an error banner: it is
      // ambient content, and it will be tried again on the next doorbell.
    }
  }

  /**
   * Drop blobs for statuses the tray no longer holds.
   *
   * The server deletes a status at its deadline and cannot tell this browser,
   * so the browser drops the same thing against the same list. A cache that
   * outlived the thing it caches would make "gone in 24 hours" false on the
   * one screen somebody would notice.
   */
  private sweepStatusMedia(): void {
    const live = new Set<string>();
    for (const entry of this.store.state.statusTray) {
      for (const item of entry.items) live.add(item.id);
    }
    for (const item of this.store.state.statusOwn) live.add(item.id);
    // A channel post's bytes come through the same loader (a post IS a
    // `statuses` row, 057), and the tray does not know them: an open channel
    // would lose its pictures at the next tray refresh without this.
    for (const post of this.store.state.channelPosts) live.add(post.id);
    this.opts.statusMedia?.sweepExpired(live);
  }

  private async viewStatus(authorId: string): Promise<void> {
    if (!authorId) return;
    this.store.viewStatus(authorId);
    await this.markCurrentStatusViewed();
  }

  private stepStatus(delta: number): void {
    const state = this.store.state;
    const entry = state.statusTray.find((e) => e.author_id === state.statusViewing);
    if (!entry) return;

    const next = state.statusIndex + delta;
    if (next < 0) return;
    if (next >= entry.items.length) {
      this.store.closeStatus();
      return;
    }
    this.store.setStatusIndex(next);
    void this.markCurrentStatusViewed();
  }

  private async markCurrentStatusViewed(): Promise<void> {
    const state = this.store.state;
    const entry = state.statusTray.find((e) => e.author_id === state.statusViewing);
    const item = entry?.items[state.statusIndex];
    if (!item || entry?.author_id === state.me?.id) return;

    // Locally first: the ring has to stop glowing the instant it is opened.
    this.store.markStatusViewed(item.id);
    try {
      await this.api.viewStatus(item.id);
    } catch {
      // The next refresh corrects it. Claiming UNSEEN would be the wrong way
      // to be wrong — it makes the ring lie about what the user just did.
    }
  }

  private cycleStatusColour(): void {
    const palette = ["#FF8A3D", "#FF3D77", "#1F6FEB", "#128C7E", "#6B4EFF", "#2D3748"];
    const current = this.store.state.statusDraft.background;
    const next = palette[(palette.indexOf(current) + 1) % palette.length];
    this.store.setStatusDraft({ background: next });
  }

  /**
   * Post through the SOCKET.
   *
   * A post has to reach the audience and only realtime can do that. A closed
   * socket is a real failure and is reported as one — quietly writing it
   * through REST would post a status nobody is told about, which looks like
   * success and is not.
   */
  private async postStatus(): Promise<void> {
    const draft = this.store.state.statusDraft;
    const body = draft.body.trim();
    if (!body) return;

    const chan = this.userChannel;
    if (!chan) {
      this.store.setStatusBusy(false, this.t("You're offline — a status has to reach people."));
      return;
    }

    this.store.setStatusBusy(true);
    try {
      await new Promise<void>((resolve, reject) => {
        chan
          .push("post_status", { kind: "text", caption: body, text_bg: draft.background })
          .receive("ok", () => resolve())
          .receive("error", (err: unknown) => reject(err))
          .receive("timeout", () => reject(new Error("timeout")));
      });
      this.store.setStatusDraft({ body: "" });
      this.store.setStatusBusy(false);
      await this.refreshStatuses();
    } catch {
      this.store.setStatusBusy(false, this.t("Couldn't post that status."));
    }
  }

  // --- Circle posts (D9, ADR-0024) ---------------------------------------

  /** Load the feed. Cheap enough to call on every arrival at Updates: the
   *  server pages it, and a stale feed is worse than a fetch. */
  /**
   * The world half of Updates: what you follow, and what to find.
   *
   * Loaded when the screen opens rather than when the segment is switched to,
   * so changing world is instant. The kinds come with it — fetched, never a
   * client copy, because the taxonomy changes by migration.
   */
  private async refreshChannels(): Promise<void> {
    try {
      const [following, suggestions] = await Promise.all([
        this.api.channels(),
        this.api.channelSuggestions(),
      ]);
      this.store.setChannels(following.channels);
      this.store.setChannelSuggestions(suggestions.channels);
    } catch {
      // Same rule as the tray and the feed: ambient content that will not load
      // is empty content, not a red banner.
    }
    if (this.store.state.publisherKinds.length === 0) {
      try {
        this.store.setPublisherKinds((await this.api.publisherKinds()).kinds);
      } catch {
        // No kinds means a suggestion row shows a name and no label, which is
        // honest. A hardcoded list would be confidently wrong instead.
      }
    }
  }

  /**
   * Open a channel's timeline, and mark it read.
   *
   * The badge clears LOCALLY at once and the request follows: the count is a
   * fact about this reader, so showing it read the moment they looked is the
   * truth rather than an optimism.
   */
  private async openChannel(channelId: string): Promise<void> {
    if (!channelId) return;
    this.store.openChannel(channelId);
    this.store.clearChannelUnread(channelId);
    void this.api.markChannelRead(channelId).catch(() => {
      // A failed mark only means the next refresh restores the count.
    });
    try {
      const page = await this.api.channelTimeline(channelId);
      this.store.setChannelPage(page.channel ?? null, page.posts, page.posts.length < CHANNEL_PAGE);
    } catch {
      this.store.setChannelError(this.t("Could not load this channel."));
    }
  }

  /** Older posts, from the oldest one held. One request in flight at a time. */
  private async loadOlderChannelPosts(): Promise<void> {
    const st = this.store.state;
    const channelId = st.channelViewing;
    const oldest = st.channelPosts[st.channelPosts.length - 1];
    if (!channelId || !oldest || st.channelAtEnd || st.channelLoadingMore) return;
    this.store.setChannelLoadingMore(true);
    try {
      const page = await this.api.channelTimeline(channelId, oldest.id);
      this.store.appendChannelPosts(page.posts, page.posts.length < CHANNEL_PAGE);
    } catch {
      this.store.setChannelError(this.t("Could not load this channel."));
    }
  }

  /**
   * ONE emoji per reader per post (ADR-0036). The pill is drawn from the
   * server's reply and never from a local +1: the count is `COUNT(*)` over
   * readers, and this browser cannot know it.
   */
  private async reactToChannelPost(postId: string, emoji: string | null): Promise<void> {
    const channelId = this.store.state.channelViewing;
    if (!channelId || !postId) return;
    try {
      this.store.setChannelReaction(postId, await this.api.reactToChannelPost(channelId, postId, emoji));
    } catch {
      this.store.setChannelPicker(null);
      this.store.setChannelNotice(this.t("Couldn't react. Try again."));
    }
  }

  /** Flip locally, ask, and put it back if the server said no — a mute is a
   *  fact about this reader, the same rule the badge follows. */
  private async setChannelMuted(muted: boolean): Promise<void> {
    const channelId = this.store.state.channelViewing;
    if (!channelId) return;
    const was = this.store.state.channelDetail?.muted ?? false;
    this.store.setChannelMuted(channelId, muted);
    try {
      await this.api.setChannelMuted(channelId, muted);
    } catch {
      this.store.setChannelMuted(channelId, was);
    }
  }

  /**
   * Forwarding is a CLIENT act (ADR-0036): the reader's own device sends the
   * post into a chat through the normal send path, so a sealed conversation
   * refuses it exactly as it refuses any other plaintext — and a server that
   * composed the message could never enter one at all.
   *
   * Text only on the web: this shell has no media send path in chats at all
   * yet, so the forward pill is not drawn on a media post rather than sending
   * a caption and calling it the photo.
   */
  private async forwardChannelPost(postId: string, chatId: string): Promise<void> {
    const post = this.store.state.channelPosts.find((p) => p.id === postId);
    this.store.setChannelForwarding(null);
    if (!post || !chatId || !this.socket) return;
    const body = (post.caption ?? "").trim();
    if (body === "") return;
    const chan = this.socket.channel(`chat:${chatId}`, {});
    const forwarded = this.t("Forwarded.");
    const failed = this.t("Couldn't forward.");
    const done = (notice: string) => {
      this.store.setChannelNotice(notice);
      chan.leave();
    };
    chan.join()
      .receive("ok", () => {
        chan.push("new_message", { body, type: "text", client_id: this.genId(), forwarded: true })
          .receive("ok", () => done(forwarded))
          .receive("error", () => done(failed))
          .receive("timeout", () => done(failed));
      })
      .receive("error", () => done(failed))
      .receive("timeout", () => done(failed));
  }

  /** Follow, then re-read BOTH lists: the followed channels and the
   *  suggestions are two halves of one answer, and only the server knows the
   *  new split. */
  private async followChannel(channelId: string): Promise<void> {
    if (!channelId) return;
    // Flip the directory row at once if it is on screen. Not optimism about
    // the network — the button describes a relationship the server has already
    // been told about, and a failed request only means the next open shows it
    // unfollowed again.
    this.store.markExploreFollowing(channelId);
    try {
      await this.api.followChannel(channelId);
    } catch {
      return;
    }
    await this.refreshChannels();
  }

  /**
   * Debounce a search, and CANCEL the previous one.
   *
   * Without the cancel, "tan" can land after "tanz" and the list shows results
   * for a query the box no longer holds.
   */
  private scheduleExplore(): void {
    if (this.exploreTimer !== null) clearTimeout(this.exploreTimer);
    this.exploreTimer = setTimeout(() => {
      this.exploreTimer = null;
      void this.exploreChannels({ reset: true });
    }, 250) as unknown as number;
  }

  /** One page of the directory (ADR-0024 A16). */
  private async exploreChannels(opts: { reset: boolean }): Promise<void> {
    const s = this.store.state;
    if (!opts.reset && (s.exploreBusy || s.exploreAtEnd)) return;
    this.store.setExploreBusy(true);
    const offset = opts.reset ? 0 : s.exploreResults.length;

    try {
      const { channels } = await this.api.channelDirectory({
        kind: s.exploreKind,
        q: s.exploreQuery === "" ? null : s.exploreQuery,
        limit: EXPLORE_PAGE,
        offset,
      });
      // A short page is the end. Asking again past the last row returns an
      // empty list forever, and a list that keeps requesting nothing never
      // stops spinning.
      this.store.setExploreResults(channels, {
        append: !opts.reset,
        atEnd: channels.length < EXPLORE_PAGE,
      });
    } catch {
      this.store.setExploreError(this.t("Could not load channels."));
    }
  }

  private async refreshFeed(): Promise<void> {
    try {
      this.store.setFeed(await this.api.postFeed());
    } catch {
      // A feed that will not load is not an error worth a red banner over the
      // tray — the empty state already says there is nothing.
    }
  }

  /**
   * Publish a post over the SOCKET.
   *
   * A closed socket is a real failure and is reported as one: falling back to
   * REST would publish a post nobody is told about, which looks like success
   * and is not. The same rule `postStatus` follows.
   */
  private async publishPost(): Promise<void> {
    const { body, audience } = this.store.state.postDraft;
    const text = body.trim();
    if (!text) return;

    const chan = this.userChannel;
    if (!chan) {
      this.store.setPostBusy(false, this.t("You're offline — a post has to reach people."));
      return;
    }

    this.store.setPostBusy(true);
    try {
      await new Promise<void>((resolve, reject) => {
        chan
          .push("post_circle", { kind: "text", caption: text, audience })
          .receive("ok", () => resolve())
          .receive("error", (err: unknown) => reject(err))
          .receive("timeout", () => reject(new Error("timeout")));
      });
      this.store.setPostDraft({ body: "" });
      this.store.setPostBusy(false);
      await this.refreshFeed();
    } catch {
      this.store.setPostBusy(false, this.t("Couldn't publish that post."));
    }
  }

  // --- Mazungumzo (D12, ADR-0025) ------------------------------------------

  /**
   * Both segments in one pass.
   *
   * `following` is asked for only when there is somebody to follow: an empty
   * Following feed is a request for a list we already know is empty, on a
   * surface whose viewers mostly follow nobody yet.
   */
  private async loadMazungumzo(): Promise<void> {
    try {
      const [ranked, follows] = await Promise.all([
        this.api.mazungumzo(),
        this.api.following(),
      ]);
      const followed = follows.users ?? [];
      const following = followed.length === 0
        ? []
        : (await this.api.mazungumzoFollowing()).posts ?? [];
      this.store.setMazungumzo(ranked.posts ?? [], following, followed);
    } catch {
      // Loaded-with-nothing rather than a spinner forever: the surface says
      // "nobody has said anything" and a refresh is one tap away.
      this.store.setMazungumzo([], [], []);
    }
  }

  // --- ADR-0029: Threads inside WaveTalk ---------------------------------

  /**
   * Repost, then RE-READ — never an optimistic flip.
   *
   * The same rule the follow button below is written under, and for a sharper
   * reason here: the COUNT beside the button also comes from the server, so a
   * local flip would leave the button saying "reposted" next to a number that
   * had not moved. Two things on screen disagreeing is worse than one of them
   * being a moment late.
   */
  private async repost(postId: string, on: boolean): Promise<void> {
    if (!postId) return;
    try {
      if (on) await this.api.repost(postId);
      else await this.api.unrepost(postId);
      await this.loadMazungumzo();
    } catch {
      this.store.setError(this.t("That didn't work. Try again."));
    }
  }

  /** Save is private and moves no number anybody else sees, so it refreshes
   *  only the list it affects. */
  private async savePost(postId: string, on: boolean): Promise<void> {
    if (!postId) return;
    try {
      if (on) await this.api.savePost(postId);
      else await this.api.unsavePost(postId);
      const saved = await this.api.savedPosts();
      this.store.setSavedPosts(saved.posts ?? []);
      await this.loadMazungumzo();
    } catch {
      this.store.setError(this.t("That didn't work. Try again."));
    }
  }

  private async openThread(postId: string): Promise<void> {
    if (!postId) return;
    try {
      const res = await this.api.postThread(postId);
      this.store.setThread(res.posts ?? []);
    } catch {
      this.store.setError(this.t("Couldn't open that thread."));
    }
  }

  private async openFeedList(list: "saved" | "liked" | "activity" | null): Promise<void> {
    this.store.setFeedList(list);
    try {
      if (list === "saved") this.store.setSavedPosts((await this.api.savedPosts()).posts ?? []);
      else if (list === "liked") this.store.setLikedPosts((await this.api.likedPosts()).posts ?? []);
      else if (list === "activity") await this.loadActivity();
    } catch {
      this.store.setError(this.t("Couldn't load that list."));
    }
  }

  private async loadActivity(filter?: string): Promise<void> {
    this.store.setActivityFilter(filter ?? null);
    try {
      const res = await this.api.activity(filter);
      this.store.setActivity(res.activity ?? []);
    } catch {
      this.store.setActivity([]);
    }
  }

  /**
   * Search public posts and people.
   *
   * Under two characters returns NOTHING without a round trip — the rule
   * `StickerSearch` already holds, because "no results" and "not searching"
   * must never render as the same list.
   */
  private async searchMazungumzo(query: string): Promise<void> {
    const q = query.trim();
    this.store.setSearchQuery(query);
    if (q.length < 2) {
      this.store.setSearchResults([], []);
      return;
    }
    try {
      const res = await this.api.searchPosts(q);
      this.store.setSearchResults(res.posts ?? [], res.people ?? []);
    } catch {
      this.store.setSearchResults([], []);
    }
  }

  /**
   * Follow or unfollow, then RE-READ.
   *
   * Never an optimistic flip. s136 paid for that on Explore's follow button:
   * the control turned to "Following" and the request had failed, and the only
   * thing that knew was a different screen.
   */
  private async followPerson(userId: string, wanted: boolean): Promise<void> {
    if (!userId) return;
    try {
      if (wanted) await this.api.followPerson(userId);
      else await this.api.unfollowPerson(userId);
    } catch {
      /* The re-read below is the oracle; a failed call simply changes nothing. */
    }
    await this.loadMazungumzo();
  }

  // --- Office (ADR-0024 D6/D7, Phase 3) ------------------------------------
  //
  // Three verbs and no more: create a publisher, turn publishing on, write a
  // post. ADR-0018 is what Office eventually contains and none of it is built.

  private async loadOffice(): Promise<void> {
    await this.loadOfficeReference();
    try {
      const { publishers } = await this.api.myPublishers();
      this.store.setPublishers(publishers ?? []);
    } catch {
      this.store.setOfficeBusy(false, this.t("Couldn't load your channels."));
    }
  }

  /** The kinds and the industries, both from the SERVER. Neither is ever a
   *  client copy — the taxonomy went from nine values to seven in one
   *  migration, and a copy is how it drifts from the CHECK enforcing it. */
  private async loadOfficeReference(): Promise<void> {
    if (this.store.state.publisherKinds.length === 0) {
      try {
        const { kinds } = await this.api.publisherKinds();
        this.store.setPublisherKinds(kinds ?? []);
      } catch {
        /* Nothing to browse is honest; a hardcoded list is confidently wrong. */
      }
    }
    if (this.store.state.businessCategories.length === 0) {
      try {
        const { categories } = await this.api.businessCategories();
        this.store.setBusinessCategories(categories ?? []);
      } catch {
        /* The industry picker simply has nothing in it. */
      }
    }
  }

  private setOfficeField(field: string, value: string | null): void {
    switch (field) {
      case "name":
        // The handle is the one field creation fixes for good, so it is
        // normalised as it is typed rather than rejected afterwards.
        this.store.setOfficeForm({ name: (value ?? "").replace(/\s+/g, "").toLowerCase() });
        return;
      case "display_name":
        this.store.setOfficeForm({ displayName: value ?? "" });
        return;
      case "description":
        this.store.setOfficeForm({ description: value ?? "" });
        return;
      case "kind":
        this.store.setOfficeForm({ kind: value });
        return;
      case "category":
        this.store.setOfficeForm({ category: value });
        return;
      case "email":
        this.store.setOfficeForm({ email: (value ?? "").trim() });
        return;
      case "phone":
        this.store.setOfficeForm({ phone: (value ?? "").trim() });
        return;
      default:
        return;
    }
  }

  private async createPublisher(): Promise<void> {
    const form = this.store.state.officeForm;
    if (!officeFormComplete(form)) {
      this.store.setOfficeBusy(false, this.t("Fill in everything above first."));
      return;
    }

    this.store.setOfficeBusy(true);
    try {
      const created = await this.api.createPublisher({
        name: form.name.trim(),
        display_name: form.displayName.trim(),
        kind: form.kind ?? "",
        email: form.email.trim(),
        phone: form.phone.trim(),
        country: "TZ",
        description: form.description.trim() || null,
        // NULL for every kind but `business` — a CHECK enforces it, and
        // sending one anyway files a ministry under an industry it does not
        // trade in (A15).
        category: form.kind === "business" ? form.category : null,
      });
      this.store.setOfficeBusy(false);
      await this.loadOffice();
      // Straight to the new channel: the next thing anybody wants after
      // creating one is to turn publishing on.
      await this.openPublisher(created.id);
    } catch (err) {
      // A handle collision is the one a person hits by accident and the only
      // one they can act on, so it is named.
      const text = String(err);
      this.store.setOfficeBusy(
        false,
        text.includes("409")
          ? this.t("That handle is already taken. Try another.")
          : "That couldn't be created. Check the details and try again."
      );
    }
  }

  private async openPublisher(publisherId: string): Promise<void> {
    if (!publisherId) return;
    this.store.openPublisher(publisherId);
    if (this.store.state.publishers.length === 0) await this.loadOffice();
    await this.reloadOfficeTimeline(publisherId);
  }

  /** Written from the SERVER's answer, never optimistically — see
   *  `Store.applyPublishing`. */
  private async setPublishing(publisherId: string, enabled: boolean): Promise<void> {
    if (!publisherId) return;
    this.store.setOfficeBusy(true);
    try {
      const result = await this.api.setPublishing(publisherId, enabled);
      this.store.applyPublishing(publisherId, result.publishes === true);
      this.store.setOfficeBusy(false);
    } catch {
      this.store.setOfficeBusy(false, this.t("Could not change that. Try again."));
    }
  }

  /**
   * Publish to a channel over the SOCKET.
   *
   * It rings every FOLLOWER, and only realtime can push to a connected client
   * — which is exactly why a REST publish from a browser rang nobody at all
   * between s135 and s138. A closed socket is a real failure and is reported
   * as one: the same rule `publishPost` follows.
   */
  private async publishToChannel(publisherId: string): Promise<void> {
    const text = this.store.state.officeDraft.trim();
    if (!publisherId || !text) return;

    const chan = this.userChannel;
    if (!chan) {
      this.store.setOfficeBusy(
        false,
        this.t("You're offline. A post has to reach your followers, so it waits.")
      );
      return;
    }

    this.store.setOfficeBusy(true);
    try {
      await new Promise<void>((resolve, reject) => {
        chan
          .push("publish_channel", { channel_id: publisherId, kind: "text", caption: text })
          .receive("ok", () => resolve())
          .receive("error", (err: unknown) => reject(err))
          .receive("timeout", () => reject(new Error("timeout")));
      });
      this.store.setOfficeDraft("");
      this.store.setOfficeBusy(false);
      await this.reloadOfficeTimeline(publisherId);
    } catch {
      this.store.setOfficeBusy(false, this.t("That post wasn't published."));
    }
  }

  // --- ADR-0018 step 1: organisations and zones -----------------------------

  private async loadOrganisations(): Promise<void> {
    try {
      const { organisations } = await this.api.organisations();
      this.store.setOrganisations(organisations ?? []);
    } catch {
      this.store.setOrgBusy(false, this.t("Couldn't load your organisations."));
    }
  }

  /**
   * One organisation's screen: the organisation, its zones and its roster.
   *
   * Three requests rather than one endpoint returning all three, because they
   * are three reads with three different lifetimes — the zone list changes
   * when somebody posts, the roster when somebody joins, and the organisation
   * itself almost never.
   */
  private async openOrganisation(orgId: string): Promise<void> {
    if (!orgId) return;
    this.store.openOrganisation(orgId);
    await this.reloadOrganisation(orgId);
  }

  private async reloadOrganisation(orgId: string): Promise<void> {
    try {
      const [org, zones, members] = await Promise.all([
        this.api.organisation(orgId),
        this.api.organisationZones(orgId),
        this.api.organisationMembers(orgId),
      ]);
      this.store.setOrganisationDetail(org, zones.zones ?? [], members.members ?? []);
      if (org.role === "owner" || org.role === "admin") await this.loadAssistant(orgId);
    } catch {
      this.store.setOrgBusy(false, this.t("Couldn't load that organisation."));
    }
  }

  /**
   * ADR-0037 E — the managers' read of the assistant and the API keys. A read
   * that could not be made is SAID, never drawn as "none": an owner shown no
   * keys would conclude there are none while one is live.
   */
  private async loadAssistant(orgId: string): Promise<void> {
    const [setup, keys] = await Promise.allSettled([
      this.api.assistantSetup(orgId),
      this.api.orgApiKeys(orgId),
    ]);
    if (this.store.state.activeOrgId !== orgId) return;
    this.store.setAssistantData(
      setup.status === "fulfilled" ? setup.value : null,
      keys.status === "fulfilled" ? keys.value.api_keys ?? [] : null,
    );
    if (setup.status === "rejected" || keys.status === "rejected") {
      this.store.setOrgBusy(false, this.t("Couldn't load that organisation."));
    }
  }

  /** Whatever the open editor is for. The server's refusal sentences are for
   *  logs, so every failure says the one generic thing. */
  private async saveOrgEditor(): Promise<void> {
    const orgId = this.store.state.activeOrgId;
    const editor = this.store.state.orgEditor;
    if (!orgId || !editor || this.store.state.orgBusy) return;

    this.store.setOrgBusy(true);
    try {
      switch (editor.kind) {
        case "assistant_name": {
          const name = editor.value.trim();
          if (!name) break;
          if (editor.creating) await this.api.createAssistant(orgId, name);
          else await this.api.renameAssistant(orgId, name);
          this.store.setOrgEditor(null);
          break;
        }
        case "zone_assistant":
          await this.setZoneAssistant(editor);
          this.store.setOrgEditor(null);
          break;
        case "key_issue": {
          const scopes = [...(editor.read ? ["zones:read"] : []), ...(editor.post ? ["zones:post"] : [])];
          const name = editor.name.trim();
          if (!name || scopes.length === 0) break;
          const issued = await this.api.issueOrgApiKey(orgId, { name, scopes, expires_in_days: editor.days });
          // The one moment the secret exists in this tab: shown once, held
          // only in the editor slot, gone when it closes.
          this.store.setOrgEditor({ kind: "key_issued", secret: issued.key, copied: false });
          break;
        }
        case "key_revoke":
          await this.api.revokeOrgApiKey(orgId, editor.key_id);
          this.store.setOrgEditor(null);
          break;
        case "key_issued":
          break;
      }
      this.store.setOrgBusy(false);
    } catch {
      this.store.setOrgBusy(false, this.t("That change wasn't made."));
    }
    await this.loadAssistant(orgId);
  }

  /**
   * On/off, shape and instructions in one zone — over the SOCKET, because a
   * seat is a roster change every member's thread draws (`member_added`), and
   * only realtime can push it to them. A blank shape or instructions clears it.
   */
  private setZoneAssistant(editor: { chat_id: string; enabled: boolean; shape: string; instructions: string }): Promise<unknown> {
    const chan = this.userChannel;
    if (!chan) return Promise.reject(new Error("offline"));
    return new Promise<unknown>((resolve, reject) => {
      chan
        .push("set_zone_assistant", {
          chat_id: editor.chat_id,
          enabled: editor.enabled,
          assistant_shape: editor.shape.trim(),
          assistant_instructions: editor.instructions.trim(),
        })
        .receive("ok", (r: unknown) => resolve(r))
        .receive("error", (err: unknown) => reject(err))
        .receive("timeout", () => reject(new Error("timeout")));
    });
  }

  private async copyIssuedKey(): Promise<void> {
    const editor = this.store.state.orgEditor;
    if (editor?.kind !== "key_issued") return;
    try {
      await navigator.clipboard.writeText(editor.secret);
      this.store.patchOrgEditor({ copied: true });
    } catch {
      // No clipboard (an insecure origin, a denied permission): the key is
      // still on screen and selectable, which is the fallback.
    }
  }

  /** REST, because it reaches nobody: the creator is its only member. */
  private async createOrganisation(): Promise<void> {
    const name = this.store.state.orgForm.name.trim();
    if (!name) return;

    this.store.setOrgBusy(true);
    try {
      const org = await this.api.createOrganisation({ name });
      this.store.setOrgBusy(false);
      // Straight into the new organisation rather than back to a list: the
      // next thing anybody wants is to put people in it.
      await this.openOrganisation(org.id);
      void this.loadOrganisations();
    } catch {
      this.store.setOrgBusy(false, this.t("That organisation wasn't created."));
    }
  }

  private async createZone(): Promise<void> {
    const orgId = this.store.state.activeOrgId;
    const { name, kind } = this.store.state.zoneForm;
    if (!orgId || !name.trim()) return;

    const reply = await this.manageOrg("zone_create", { name: name.trim(), kind });
    // Opening the new zone needs the chat id the SERVER generated. A client
    // that invented one would be opening a conversation that does not exist.
    const chatId = reply && typeof reply === "object" ? (reply as { chat_id?: string }).chat_id : null;
    if (chatId) {
      // Known as a ZONE before it opens: without a row, `areaOf` reads it as
      // Chats — rail, list column and Back — until some other answer brings
      // one (`api.zone`, or the organisation's own zone list). Every field
      // here is what we just asked the server to create; the fetch in
      // `openChat` does not run for a row we hold, so the org's zones are
      // reloaded too.
      this.store.rememberZoneRow({
        id: chatId, name: name.trim(), chat_type: "zone", zone_kind: kind, org_id: orgId,
        unread_count: 0, last_message: null,
      });
      void this.reloadOrganisation(orgId);
      await this.openChat(chatId);
    } else {
      this.store.openOrganisation(orgId);
    }
  }

  private async leaveOrganisation(): Promise<void> {
    const orgId = this.store.state.activeOrgId;
    if (!orgId) return;

    this.store.setOrgBusy(true);
    try {
      await this.api.leaveOrganisation(orgId);
      this.store.setOrgBusy(false);
      this.store.forgetOrganisation(orgId);
    } catch {
      // The owner may not leave, and the server is what refuses it — this
      // client does not pre-empt the refusal, because a second rule is a rule
      // to keep in step with the first.
      this.store.setOrgBusy(false, this.t("You couldn't leave that organisation."));
    }
  }

  /**
   * The Office mutations that reach somebody else's screen.
   *
   * ONE socket verb with an `op`, matching the server's `manage_org`. The API
   * cannot push to a connected client, so adding a person, removing one,
   * changing a role and creating a zone all go socket → `/internal/office/…` →
   * `organisation_updated` on every affected `user:<id>`.
   */
  private async manageOrg(op: string, extra: Record<string, unknown>): Promise<unknown> {
    const orgId = this.store.state.activeOrgId;
    if (!orgId) return null;

    const chan = this.userChannel;
    if (!chan) {
      this.store.setOrgBusy(false, this.t("You're offline."));
      return null;
    }

    this.store.setOrgBusy(true);
    try {
      const reply = await new Promise<unknown>((resolve, reject) => {
        chan
          .push("manage_org", { org_id: orgId, op, ...extra })
          .receive("ok", (r: unknown) => resolve(r))
          .receive("error", (err: unknown) => reject(err))
          .receive("timeout", () => reject(new Error("timeout")));
      });
      this.store.setOrgBusy(false);
      await this.reloadOrganisation(orgId);
      return reply;
    } catch {
      this.store.setOrgBusy(false, this.t("That change wasn't made."));
      return null;
    }
  }

  private async reloadOfficeTimeline(publisherId: string): Promise<void> {
    try {
      const { posts } = await this.api.channelTimeline(publisherId);
      this.store.setOfficeTimeline(posts ?? []);
    } catch {
      this.store.setOfficeTimeline([]);
    }
  }

  /** A post IS a status row, so this is the status like route — one code path
   *  for liking one row, however it is being shown. */
  private async likePost(postId: string, liked: boolean): Promise<void> {
    if (!postId) return;
    this.store.setPostLiked(postId, liked);
    try {
      await this.api.likeStatus(postId, liked);
    } catch {
      this.store.setPostLiked(postId, !liked);
    }
  }

  private async deletePost(postId: string): Promise<void> {
    if (!postId) return;
    try {
      await this.api.deleteStatus(postId);
      this.store.dropPost(postId);
    } catch {
      this.store.setPostBusy(false, this.t("Couldn't delete that post."));
    }
  }

  private async sendPostComment(postId: string): Promise<void> {
    const body = this.store.state.feedDraft.trim();
    if (!postId || !body) return;

    try {
      await this.api.createPostComment(postId, body);
      this.store.setCommenting(null);
      const { comments } = await this.api.postComments(postId);
      this.store.setPostComments(postId, comments);
    } catch {
      this.store.setPostBusy(false, this.t("Couldn't post that comment."));
    }
  }

  private async deletePostComment(postId: string, commentId: string): Promise<void> {
    if (!commentId) return;
    try {
      await this.api.deletePostComment(commentId);
      const { comments } = await this.api.postComments(postId);
      this.store.setPostComments(postId, comments);
    } catch {
      this.store.setPostBusy(false, this.t("Couldn't delete that comment."));
    }
  }

  private async likeStatus(statusId: string, liked: boolean): Promise<void> {
    if (!statusId) return;
    this.store.setStatusLiked(statusId, liked);
    try {
      await this.api.likeStatus(statusId, liked);
    } catch {
      this.store.setStatusLiked(statusId, !liked);
    }
  }

  private async deleteStatus(statusId: string): Promise<void> {
    if (!statusId) return;
    try {
      await this.api.deleteStatus(statusId);
      this.store.closeStatus();
      await this.refreshStatuses();
    } catch {
      this.store.setStatusBusy(false, this.t("Couldn't delete that."));
    }
  }

  private async muteStatusAuthor(authorId: string): Promise<void> {
    if (!authorId) return;
    try {
      await this.api.muteStatusAuthor(authorId, true);
      this.store.closeStatus();
      await this.refreshStatuses();
    } catch {
      this.store.setStatusBusy(false, this.t("Couldn't mute that person."));
    }
  }

  /**
   * Report the status being viewed.
   *
   * The status id goes on the wire, not just the author: a status expires in
   * 24 hours, so a report naming only the account leaves whoever works the
   * queue with no evidence — and it is what makes the Ops take-down possible.
   */
  private async reportStatus(statusId: string, reason: string): Promise<void> {
    const entry = this.store.state.statusTray.find(
      (e) => e.author_id === this.store.state.statusViewing,
    );
    if (!statusId || !entry) return;
    this.store.reportStatus(null);
    try {
      await this.api.reportUser(entry.author_id, reason, { statusId });
      this.store.setStatusNotice(this.t("Reported. Thank you — we'll review it."));
    } catch {
      this.store.setStatusNotice(this.t("Couldn't send the report. Try again."));
    }
  }

  private async setStatusPolicy(policy: string): Promise<void> {
    const current = this.store.state.statusAudience;
    try {
      const next = await this.api.setPrivacy("status", policy, {
        except: (current?.except ?? []).map((m) => m.user_id),
        only: (current?.only ?? []).map((m) => m.user_id),
      });
      this.store.setStatusAudience(next);
    } catch {
      this.store.setStatusBusy(false, this.t("Couldn't change who can see your status."));
    }
  }

  private chatRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  private scheduleChatRefresh(): void {
    if (this.chatRefreshTimer) return;
    this.chatRefreshTimer = setTimeout(() => {
      this.chatRefreshTimer = null;
      void this.api.listChats().then(
        async (chats) => this.store.setChats(await this.withLocalPreviews(chats)),
        () => undefined,
      );
    }, 400);
  }

  private leaveActive(): void {
    if (this.activeChannel && this.socket) {
      this.activeChannel.leave();
      this.socket.remove(this.activeChannel);
    }
    this.activeChannel = null;
    this.typingSent = false;
  }

  // --- compose + send ------------------------------------------------------

  private onDraft(value: string): void {
    this.store.setDraft(value);
    const typing = value.trim().length > 0;
    if (typing !== this.typingSent && this.activeChannel) {
      this.typingSent = typing;
      this.activeChannel.push("typing", { is_typing: typing });
    }
  }

  private doSend(): void {
    const body = this.store.state.draft.trim();
    const chatId = this.store.state.activeChatId;
    const me = this.store.state.me;
    if (!body || !chatId || !me || !this.activeChannel) return;

    const clientId = this.genId();
    const echo: Message = {
      id: `tmp-${clientId}`,
      client_msg_id: clientId,
      chat_id: chatId,
      user_id: me.id,
      body,
      message_type: "text",
      created_at: this.now(),
      pending: true,
    };
    this.store.upsertMessage(echo);
    this.store.clearDraft();
    if (this.typingSent) {
      this.typingSent = false;
      this.activeChannel.push("typing", { is_typing: false });
    }

    this.activeChannel
      .push("new_message", { body, type: "text", client_id: clientId })
      .receive("ok", (m) => this.store.upsertMessage({ ...normalize(m), pending: false }))
      .receive("error", (err) => this.onSendRefused(err, clientId, body));
  }

  /**
   * ADR-0027 D2d — the server refused the send, and this surface has to say
   * which kind of refusal it was.
   *
   * This is the only place web can learn that a chat is end-to-end encrypted.
   * Joana has NO per-chat encryption UI at all — no banner, no lock, no "Turn
   * on" — so until it becomes a real linked device (D4, stage 3) the refusal
   * frame is the whole of what this surface knows about encryption.
   *
   * Matched on `code` and never on `reason`: the sentence beside it is prose
   * for a log, and prose gets translated. Same rule the phones follow.
   *
   * The echo goes and the text comes BACK TO THE BOX on this one refusal,
   * because it can never succeed while the flag stands. Web has no outbox and
   * no failed state to draw, so a pending bubble here would sit forever
   * claiming to be on its way — which is the same class of untruth the banner
   * was telling in s150. A transient failure keeps the old behaviour: the
   * message may yet arrive, and yanking the text out from under someone whose
   * network blipped would be its own lie.
   */
  private onSendRefused(err: unknown, clientId: string, body: string): void {
    const code = str((err as { code?: unknown } | null)?.code);

    if (code === "chat_is_encrypted") {
      this.store.removeMessage(clientId);
      this.store.setDraft(body);
      // The server just told us something the header may not have known yet.
      // Recording it means the lock appears on the refusal even if the state
      // request failed, and it is the more authoritative of the two answers.
      const chatId = this.store.state.activeChatId;
      if (chatId) this.store.setChatE2E(chatId, true);
      // Two sentences, because two things are true and they stopped being the
      // same thing in s157. A LINKED browser reads this conversation
      // perfectly well and only cannot send into it; an unlinked one cannot do
      // either. The old single sentence said "you can only read and write it
      // on your phone", which has been half wrong since the day this browser
      // became a Signal device.
      this.store.setError(
        this.store.state.signalDeviceLinked === true
          ? this.t("You can read this chat here, but sending is only on your phone for now.")
          : this.t("This chat is end-to-end encrypted. You can only read and write it on your phone."),
      );
      return;
    }

    this.store.setError(this.t("Message failed to send"));
  }
}

/**
 * The server's required set for a publisher, mirrored so a refusal shows before
 * the round trip rather than as a 400 with a field name in it. `account_type`
 * is not in it and must never be — see `Publisher.account_type`.
 */
function officeFormComplete(form: ShellState["officeForm"]): boolean {
  const business = form.kind === "business";
  return (
    form.name.trim().length >= 3 &&
    form.displayName.trim() !== "" &&
    (form.kind ?? "") !== "" &&
    form.email.includes("@") &&
    form.phone.trim() !== "" &&
    (!business || (form.category ?? "") !== "")
  );
}

function normalize(m: unknown): Message {
  const msg = m as Message;
  return { ...msg, pending: false };
}

/**
 * One decrypted message, in the shape the renderer already draws (ADR-0027
 * D4.6).
 *
 * `id` is the sender's `client_msg_id` and so is `client_msg_id`, deliberately
 * both: an E2E message has no server row and therefore no server id, and the
 * client id is what BOTH ends key it by — the sender's optimistic row, the
 * recipient's decrypted row, and A10's delete-for-everyone frame. Filling only
 * one of the two fields would make the store's dedup miss a redelivery, which
 * store-and-forward makes an ordinary event rather than a rare one.
 *
 * An envelope this browser could not read becomes a message that SAYS so. The
 * alternative — dropping it — is the silence that makes a missing message
 * unreportable, and the reader cannot tell it from nothing having been sent.
 *
 * The reply target and the forwarded flag are decoded and STORED but not mapped
 * here, because this surface renders neither on the plaintext path either: chat
 * `Message` has no reply field and `views.ts` draws no quote. Carrying them
 * through the cache anyway means the day web gains a quote, the messages it
 * already holds have one — rather than the metadata having been thrown away at
 * the only point it existed.
 */
function toMessage(m: DecryptedMessage): Message {
  return {
    id: m.clientMsgId,
    client_msg_id: m.clientMsgId,
    chat_id: m.chatId,
    user_id: m.senderUserId,
    body: m.undecryptable ? null : m.body,
    // The type is what the renderer switches on, so it has to be the type the
    // PLAINTEXT path would have produced — a sticker that arrived encrypted is
    // still a sticker, and `text` would draw its empty body.
    // A history frame (ADR-0027 D4.7) states its own type, because the messages
    // a phone re-sends were written before the chat was flagged and can be an
    // image or a voice note — neither of which a LIVE encrypted message can be
    // yet. Live messages carry no type and fall through to the same two answers
    // this always gave.
    message_type: m.undecryptable
      ? "undecryptable"
      : m.stickerRef
        ? "sticker"
        : (m.type && m.type !== "text" ? m.type : "text"),
    metadata: m.stickerRef ? { sticker: m.stickerRef } : undefined,
    media_url: m.mediaUrl ?? undefined,
    created_at: m.createdAt,
    pending: false,
  };
}

function str(v: unknown): string {
  return v == null ? "" : String(v);
}

/**
 * ADR-0030 D6 — one line carrying the three facts a search row must carry.
 *
 * The picker this feeds takes a single string, so the handle and the marker
 * are appended rather than stacked; the CONTENT is the same on every surface.
 * A reachable account contributes nothing, which is what keeps the marker a
 * one-directional disclosure (D4).
 */
function personLabel(
  u: { username: string | null; display_name: string | null; reachability?: string | null },
  t: (key: string, ...args: (string | number)[]) => string,
): string {
  const name = u.display_name ?? (u.username ? "@" + u.username : t("Someone"));
  const handle = u.display_name && u.username ? " @" + u.username : "";
  const marker = u.reachability === "quiet"
    ? " · " + t("Not used recently")
    : u.reachability === "unused"
    ? " · " + t("Never sent a message")
    : "";
  return name + handle + marker;
}

function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : String(e);
}

// Real timers — the browser default. Tests inject a manual scheduler instead.
function defaultScheduler(): Scheduler {
  const g = globalThis as unknown as {
    setTimeout: (fn: () => void, ms: number) => unknown;
    clearTimeout: (h: unknown) => void;
  };
  return {
    setTimeout: (fn, ms) => g.setTimeout(fn, ms),
    clearTimeout: (h) => g.clearTimeout(h),
  };
}

function cryptoRandomId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  // Fallback (non-crypto) — only hit where Web Crypto is unavailable.
  return `id-${Math.abs(hashNow())}-${seqId()}`;
}

let seq = 0;
function seqId(): number {
  return (seq = (seq + 1) % Number.MAX_SAFE_INTEGER);
}
function hashNow(): number {
  // Avoid Date.now in environments that forbid it; fall back to a counter.
  return seqId() * 2654435761;
}
